import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE_URL } from "../config";
import {
  FaFileAlt,
  FaFileExcel,
  FaPrint,
  FaFilePdf,
  FaUpload,
  FaChevronUp,
  FaChevronDown,
  FaSearch,
  FaEdit,
  FaTrash,
  FaTrashAlt,
  FaLock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimes,
  FaPlus,
  FaFileInvoiceDollar,
  FaCoins,
  FaPhone,
  FaMapMarkerAlt,
  FaUser,
  FaUndo
} from "react-icons/fa";
import ExcelJS from "exceljs";
import { useAuth } from '../components/shared/AuthContext';
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import LoanStatementPDF from './Loans/LoanStatementPDF';
import { useNavigate } from "react-router-dom";
import Logo from "../assets/logo_jothi.jpg";
// Box and Tooltip only. Button is imported from ../components/ui below, and
// pulling MUI's Button in here would shadow it and restyle every button on the
// page.
import { Box, Tooltip, Typography } from "@mui/material";

import {
  PageLayout,
  PageHeader,
  Button,
  Card,
  StatCard,
  StatCardGrid,
  Table,
  Badge,
  Modal,
  Spinner,
  SearchInput,
  EmptyState,
  DatePicker,
  useSnackbar
} from "../components/ui";
import FormInput from "../components/ui/FormInput";
// member_details.amountToRelease stores PAISE since money-to-paise-bigint-stage2;
// loanstatement is still rupees, so the two must be reconciled on read.
import { toRupees } from "../utils/money";

// Add retry axios configuration
const axiosWithRetry = axios.create();
axiosWithRetry.interceptors.response.use(undefined, async (err) => {
  const { config, message } = err;
  if (!config || !config.retry) {
    return Promise.reject(err);
  }

  config.retryCount = config.retryCount || 0;

  if (config.retryCount >= config.retry) {
    return Promise.reject(err);
  }

  config.retryCount += 1;
  console.log(`Retrying API call (${config.retryCount}/${config.retry}): ${config.url}`);

  const backoff = new Promise((resolve) => {
    setTimeout(() => resolve(), config.retryDelay || 1000);
  });

  await backoff;
  return axiosWithRetry(config);
});

// Helper function for API calls
const apiCall = async (url, method = 'get', data = null, options = {}) => {
  try {
    const config = {
      ...options,
      retry: 2,
      retryDelay: 1000,
      timeout: 10000
    };

    let response;
    if (method.toLowerCase() === 'get') {
      response = await axiosWithRetry.get(url, config);
    } else if (method.toLowerCase() === 'post') {
      response = await axiosWithRetry.post(url, data, config);
    }

    return response.data;
  } catch (error) {
    console.error(`API Error (${url}):`, error.message);
    throw error;
  }
};

// Utility function to format dates (DD.MM.YYYY to match parseCustomDate)
const formatDate = (dateString) => {
  if (!dateString) return "N/A";

  // proposed_loan_details.emiDate is a varchar already holding DD.MM.YYYY, and
  // new Date() reads dotted dates as MM.DD.YYYY: '13.05.2026' has no month 13
  // and rendered the literal "Invalid Date" (565 loans), while '03.09.2026'
  // parsed as 9 March instead of 3 September — wrong, silently (382 loans).
  // The stored format is already the display format, so pass it straight
  // through rather than round-tripping it through Date.
  const dotted = String(dateString).match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (dotted) {
    const [, day, month, year] = dotted;
    if (Number(month) >= 1 && Number(month) <= 12 && Number(day) >= 1 && Number(day) <= 31) {
      return `${day}.${month}.${year}`;
    }
    return "Invalid Date";
  }

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Invalid Date";
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

// Utility function to format currency
const formatCurrency = (amount) => {
  return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const getLoanDetails = async (selectedOption) => {
  if (!selectedOption) {
    console.log("No selected option provided.");
    return null;
  }

  try {
    console.log("Fetching loan details for customer:", selectedOption.customerUniqueId);

    const response = await fetch(
      `${API_BASE_URL}/api/v2/member-details?customerUniqueId=${selectedOption.customerUniqueId}`,
      {
        signal: AbortSignal.timeout(15000),
      }
    );

    if (!response.ok) {
      console.warn("V2 API failed, falling back to legacy API");
      return await getLoanDetailsLegacy(selectedOption);
    }

    const result = await response.json();

    if (result.status !== 'success' || !result.data) {
      console.error("API returned error:", result);
      return null;
    }

    const { personalDetails, members, transactions } = result.data;

    if (!personalDetails) {
      console.log(`No personal details found for customerUniqueId: ${selectedOption.customerUniqueId}`);
      return null;
    }

    if (!members || members.length === 0) {
      console.log(`No member details found for customerUniqueId: ${selectedOption.customerUniqueId}`);
      return null;
    }

    const allLoans = [];
    const today = new Date();

    for (const member of members) {
      const memberId = member.id;
      if (!memberId) continue;

      const loanDetail = member.proposedLoan;
      const memberLoanStatements = member.loanStatements || [];

      // PAISE -> RUPEES. This feeds totalLoanAmount, which drives the Principal
      // Amount card and the ACTIVE badge. Both sat beside an Interest card summed
      // from loanstatement (still rupees), so a 3,00,000 loan read as
      // 3,00,00,000 next to a correct 1,15,920 — two units in one card row.
      const loanAmount = Math.round(toRupees(member.amountToRelease)) || 0;
      const tenure = loanDetail ? parseInt(loanDetail.tenureInMonths) || 0 : 0;
      const emiDateStr = loanDetail ? loanDetail.emiDate : new Date().toISOString();

      let paymentData = [];
      let isLoanClosed = false;
      let isForeclosed = false;
      const finishedEmiDates = [];
      const upcomingEmiDates = [];

      if (memberLoanStatements && memberLoanStatements.length > 0) {
        memberLoanStatements.sort((a, b) => a.dueNo - b.dueNo);

        isLoanClosed = memberLoanStatements.every(statement =>
          (statement.received || '').toString().trim().toLowerCase() === "received"
        );

        isForeclosed = memberLoanStatements.some(statement =>
          (statement.received || '').toString().trim().toLowerCase() === "foreclosed"
        ) || member.loanStatus === 'foreclosed';

        paymentData = memberLoanStatements.map(statement => {
          const dueDate = new Date(statement.dueDate);

          const dueDateString = formatDate(statement.dueDate);
          const paymentDateString = statement.paymentDate
            ? `${formatDate(statement.paymentDate)} ${new Date(statement.paymentDate).toLocaleTimeString()}`
            : "N/A";

          if (dueDate < today) {
            finishedEmiDates.push(statement.dueDate);
          } else {
            upcomingEmiDates.push(statement.dueDate);
          }

          return {
            id: statement.id,
            memberId: memberId,
            dueDate: dueDateString,
            dueNo: statement.dueNo,
            paymentDate: paymentDateString,
            principal: statement.principal,
            interest: statement.interest,
            total: statement.total,
            arrearPrincipal: statement.arrearPrincipal,
            arrearInterest: statement.arrearInterest,
            arrearTotal: statement.arrearTotal,
            received: statement.received,
            penalty: statement.penalty,
            paid: statement.penaltyPaid,
            emiReceived: statement.emiReceived,
            outstanding: statement.outstanding
          };
        });
      }

      // Always honour loanStatus from member_details regardless of statement state.
      // 'preclosed' = settled early; 'foreclosed' may have no statements if data was imported.
      // 'closed' = ran its full term; 'preclosed' = settled early. Both are over.
      if (member.loanStatus === 'preclosed' || member.loanStatus === 'closed') isLoanClosed = true;
      if (member.loanStatus === 'foreclosed') isForeclosed = true;

      if (paymentData.length === 0 && loanAmount > 0) {
        console.log(`No loanstatement records for member ${memberId} (loanAmount: ${loanAmount}). Showing empty state.`);
      }

      allLoans.push({
        memberId: memberId,
        loanDate: formatDate(emiDateStr),
        totalLoanAmount: loanAmount,
        monthlyEmi: paymentData.length > 0 ? paymentData[0]?.total || 0 : 0,
        tenureInMonths: tenure.toString(),
        interestPattern: "Exact Decreasing",
        finishedEmiDates,
        upcomingEmiDates,
        paymentData,
        isLoanClosed,
        isForeclosed,
        securityDeposit: member.securityDeposit || 0,
        gst: member.gst || 0,
        loanStatus: member.loanStatus
      });
    }

    allLoans.sort((a, b) => {
      if (!a.isLoanClosed && b.isLoanClosed) return -1;
      if (a.isLoanClosed && !b.isLoanClosed) return 1;
      return new Date(b.loanDate) - new Date(a.loanDate);
    });

    if (allLoans.length === 0) {
      console.log("No valid loans found for the customer");
      return null;
    }

    return {
      customerName: personalDetails.name || `${personalDetails.firstName || ''} ${personalDetails.lastName || ''}`.trim(),
      customerId: personalDetails.customerUniqueId,
      loans: allLoans,
      ...allLoans[0]
    };
  } catch (error) {
    console.error("Error fetching loan details:", error);
    try {
      return await getLoanDetailsLegacy(selectedOption);
    } catch (legacyError) {
      console.error("Legacy API also failed:", legacyError);
      return null;
    }
  }
};

const getLoanDetailsLegacy = async (selectedOption) => {
  if (!selectedOption) {
    return null;
  }

  try {
    const personalResponse = await fetch(
      `${API_BASE_URL}/api/getModelData/personal_details?filter=${encodeURIComponent(JSON.stringify({ customerUniqueId: selectedOption.customerUniqueId }))}`,
      { signal: AbortSignal.timeout(10000) }
    );
    if (!personalResponse.ok) {
      throw new Error(`Failed to fetch personal details: ${personalResponse.statusText}`);
    }
    const personalDataObj = await personalResponse.json();

    if (!personalDataObj || !Array.isArray(personalDataObj.data) || personalDataObj.data.length === 0) {
      return null;
    }

    const personalDetail = personalDataObj.data[0];
    const personalId = personalDetail.id;

    const memberResponse = await fetch(
      `${API_BASE_URL}/api/getModelData/member_details?filter=${encodeURIComponent(JSON.stringify({ customerId: personalId }))}`,
      { signal: AbortSignal.timeout(10000) }
    );
    if (!memberResponse.ok) {
      throw new Error(`Failed to fetch member details: ${memberResponse.statusText}`);
    }
    const memberDataObj = await memberResponse.json();

    if (!memberDataObj || !Array.isArray(memberDataObj.data) || memberDataObj.data.length === 0) {
      return null;
    }

    const memberDetails = memberDataObj.data;
    const memberIds = memberDetails.map(m => m.id);

    const loanStatementResponse = await fetch(
      `${API_BASE_URL}/api/getModelData/loanstatement?filter=${encodeURIComponent(JSON.stringify({ memberId: memberIds }))}`,
      { signal: AbortSignal.timeout(10000) }
    );
    const loanStatementObj = loanStatementResponse.ok ? await loanStatementResponse.json() : { data: [] };
    const loanStatementData = loanStatementObj.data || [];

    const proposedLoanResponse = await fetch(
      `${API_BASE_URL}/api/getModelData/proposed_loan_details?filter=${encodeURIComponent(JSON.stringify({ memberId: memberIds }))}`,
      { signal: AbortSignal.timeout(10000) }
    );
    const proposedLoanObj = proposedLoanResponse.ok ? await proposedLoanResponse.json() : { data: [] };
    const proposedLoanData = proposedLoanObj.data || [];

    const allLoans = [];
    const today = new Date();

    for (const memberDetail of memberDetails) {
      const memberId = memberDetail.id;
      if (!memberId) continue;

      const loanDetail = proposedLoanData.find((detail) => detail.memberId === memberId);
      const memberLoanStatements = loanStatementData.filter(statement => statement.memberId === memberId);

      // PAISE -> RUPEES, same as the v2 path above. This is the legacy fallback
      // (getLoanDetailsLegacy); missing it here would make the same screen right
      // or wrong depending on which fetch path served it.
      const loanAmount = Math.round(toRupees(memberDetail.amountToRelease)) || 0;
      const tenure = loanDetail ? parseInt(loanDetail.tenureInMonths) || 0 : 0;
      const emiDateStr = loanDetail ? loanDetail.emiDate : new Date().toISOString();

      let paymentData = [];
      let isLoanClosed = false;
      let isForeclosed = false;
      const finishedEmiDates = [];
      const upcomingEmiDates = [];

      if (memberLoanStatements && memberLoanStatements.length > 0) {
        memberLoanStatements.sort((a, b) => a.dueNo - b.dueNo);
        isLoanClosed = memberLoanStatements.every(statement =>
          (statement.received || '').toString().trim().toLowerCase() === "received"
        );

        isForeclosed = memberLoanStatements.some(statement =>
          (statement.received || '').toString().trim().toLowerCase() === "foreclosed"
        ) || memberDetail.loanStatus === 'foreclosed';

        paymentData = memberLoanStatements.map(statement => {
          const dueDate = new Date(statement.dueDate);
          const dueDateString = formatDate(statement.dueDate);
          const paymentDateString = statement.paymentDate
            ? `${formatDate(statement.paymentDate)} ${new Date(statement.paymentDate).toLocaleTimeString()}`
            : "N/A";

          if (dueDate < today) {
            finishedEmiDates.push(statement.dueDate);
          } else {
            upcomingEmiDates.push(statement.dueDate);
          }

          return {
            id: statement.id,
            memberId: memberId,
            dueDate: dueDateString,
            dueNo: statement.dueNo,
            paymentDate: paymentDateString,
            principal: statement.principal,
            interest: statement.interest,
            total: statement.total,
            arrearPrincipal: statement.arrearPrincipal,
            arrearInterest: statement.arrearInterest,
            arrearTotal: statement.arrearTotal,
            received: statement.received,
            penalty: statement.penalty,
            paid: statement.penaltyPaid,
            emiReceived: statement.emiReceived,
            outstanding: statement.outstanding
          };
        });
      }

      if (memberDetail.loanStatus === 'preclosed' || memberDetail.loanStatus === 'closed') isLoanClosed = true;
      if (memberDetail.loanStatus === 'foreclosed') isForeclosed = true;

      allLoans.push({
        memberId: memberId,
        loanDate: formatDate(emiDateStr),
        totalLoanAmount: loanAmount,
        monthlyEmi: paymentData.length > 0 ? paymentData[0]?.total || 0 : 0,
        tenureInMonths: tenure.toString(),
        interestPattern: "Exact Decreasing",
        finishedEmiDates,
        upcomingEmiDates,
        paymentData,
        isLoanClosed,
        isForeclosed,
        securityDeposit: memberDetail.securityDeposit || 0,
        gst: memberDetail.gst || 0,
        loanStatus: memberDetail.loanStatus
      });
    }

    allLoans.sort((a, b) => {
      if (!a.isLoanClosed && b.isLoanClosed) return -1;
      if (a.isLoanClosed && !b.isLoanClosed) return 1;
      return new Date(b.loanDate) - new Date(a.loanDate);
    });

    if (allLoans.length === 0) {
      return null;
    }

    return {
      customerName: personalDetail.name || `${personalDetail.firstName || ''} ${personalDetail.lastName || ''}`.trim(),
      customerId: personalDetail.customerUniqueId,
      loans: allLoans,
      ...allLoans[0]
    };
  } catch (error) {
    console.error("Error fetching loan details (legacy):", error);
    return null;
  }
};

const parseCustomDate = (dateString) => {
  if (!dateString) return null;
  const [day, month, year] = dateString.split(".");
  return new Date(`${year}-${month}-${day}`);
};

const isPaymentPending = (received) => {
  const val = (received || '').toString().trim().toLowerCase();
  return val !== 'received' && val !== 'foreclosed';
};

/* ==================== SUB-COMPONENT: LoanStatement ==================== */
const LoanStatement = ({ selectedOption, showLoanStatement, setLoanData, accountType }) => {
  const [loadingLoanDetails, setLoadingLoanDetails] = useState(false);
  const [showAllEmis, setShowAllEmis] = useState(false);
  const [loanData, setLocalLoanData] = useState(null);
  const [openPdfModal, setOpenPdfModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [forceRefresh, setForceRefresh] = useState(0);
  const [openForeclosureModal, setOpenForeclosureModal] = useState(false);
  const [foreclosureData, setForeclosureData] = useState({
    balancePrincipal: 0,
    preclosureRate: 6,
    preclosureCharge: 0,
    gstType: 'percentage',
    gstPercentage: 8,
    gst: 0,
    bounceCharges: 0,
    securityDeposit: 0,
    totalAmount: 0,
    narration: 'Loan Foreclosure - Full Settlement of All Pending EMIs',
    paymentDate: new Date().toISOString().split('T')[0]
  });
  const [processingForeclosure, setProcessingForeclosure] = useState(false);
  const [showForeclosureConfirm, setShowForeclosureConfirm] = useState(false);
  const [memberClosingBalance, setMemberClosingBalance] = useState(null);
  const [fetchingClosingBalance, setFetchingClosingBalance] = useState(false);
  const [activeFilter, setActiveFilter] = useState('active');

  // Admin edit/delete state for loan statement
  const [editLoanModalOpen, setEditLoanModalOpen] = useState(false);
  const [editLoanData, setEditLoanData] = useState(null);
  const [savingLoanEdit, setSavingLoanEdit] = useState(false);
  const [addLoanModalOpen, setAddLoanModalOpen] = useState(false);
  const [addLoanData, setAddLoanData] = useState({
    dueNo: '',
    dueDate: new Date().toISOString().split('T')[0],
    principal: 0,
    interest: 0,
    total: 0,
    arrearPrincipal: 0,
    arrearInterest: 0,
    arrearTotal: 0,
    received: 'Pending',
    penalty: 0,
    penaltyPaid: 0,
    emiReceived: 0,
    outstanding: 0,
  });
  const [savingNewLoan, setSavingNewLoan] = useState(false);

  const hasActiveLoan = useMemo(() => {
    return loanData?.loans?.some(l => !l.isLoanClosed && !l.isForeclosed && l.loanStatus !== 'foreclosed') || false;
  }, [loanData]);

  const hasClosedLoan = useMemo(() => {
    return loanData?.loans?.some(l => l.isLoanClosed || l.isForeclosed || l.loanStatus === 'foreclosed') || false;
  }, [loanData]);

  const handleLoanSwitch = (loanIndex) => {
    if (!loanData?.loans || loanIndex < 0 || loanIndex >= loanData.loans.length) return;

    const selectedLoan = loanData.loans[loanIndex];
    if (selectedLoan) {
      const newFilter = (selectedLoan.isForeclosed || selectedLoan.loanStatus === 'foreclosed') ? 'closed' :
        selectedLoan.isLoanClosed ? 'closed' : 'active';
      setActiveFilter(newFilter);

      const updatedData = {
        ...loanData,
        ...selectedLoan,
        currentLoanIndex: loanIndex
      };

      setLocalLoanData(updatedData);

      if (setLoanData) {
        setLoanData(updatedData);
      }
    }
  };

  const isDueDateInFuture = (dueDateString) => {
    if (!dueDateString) return false;
    const dueDate = parseCustomDate(dueDateString);
    if (!dueDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return dueDate > today;
  };

  useEffect(() => {
    const fetchLoanData = async () => {
      if (showLoanStatement && selectedOption) {
        setLoadingLoanDetails(true);
        try {
          const data = await getLoanDetails(selectedOption);
          setLocalLoanData(data);
          setLoanData(data);
        } catch (error) {
          console.error('Error fetching loan data:', error);
          setLocalLoanData(null);
          setLoanData(null);
          showNotification('Failed to load loan details. Please try again.', 'error');
        } finally {
          setLoadingLoanDetails(false);
        }
      }
    };
    fetchLoanData();
  }, [showLoanStatement, selectedOption, setLoanData]);

  const getLoanStatus = () => {
    if (!loanData) return 'unknown';

    if (loanData.isForeclosed ||
      loanData.loanStatus === 'foreclosed' ||
      loanData.completionStatus === 'Foreclosed' ||
      loanData.loanDetails?.completionStatus === 'Foreclosed' ||
      loanData.foreclosureDate) {
      return 'foreclosed';
    }

    if (loanData.isLoanClosed) {
      return 'closed';
    }

    if (loanData.completionStatus === 'Closed' ||
      loanData.loanDetails?.completionStatus === 'Closed' ||
      (loanData.paymentData && loanData.paymentData.length > 0 &&
        loanData.paymentData.every(payment => (payment.received || '').toString().trim().toLowerCase() === 'received'))) {
      return 'closed';
    }

    return 'active';
  };

  const loanStatus = useMemo(() => getLoanStatus(), [loanData]);

  useEffect(() => {
    if (loanData) {
      const status = getLoanStatus();
      setActiveFilter(status === 'foreclosed' ? 'closed' : status);
    }
  }, [loanData]);

  const filteredPaymentData = useMemo(() => {
    if (!loanData?.paymentData) return [];

    const paymentData = JSON.parse(JSON.stringify(loanData.paymentData));
    const sortedData = paymentData.sort((a, b) => a.dueNo - b.dueNo);
    const effectiveStatus = (loanStatus === 'foreclosed') ? 'closed' : loanStatus;

    if (activeFilter !== 'all' && activeFilter !== effectiveStatus) {
      return [];
    }

    return sortedData;
  }, [loanData, loanStatus, activeFilter, forceRefresh]);

  // Oldest unpaid installment for this loan. EMIs must be settled strictly
  // oldest-first — collectEmi.js rejects an out-of-order payment with HTTP 409
  // ("Settle EMI #N first"), so only that due may show an enabled action.
  // Math.min (rather than "first element") keeps this correct regardless of sort order.
  const oldestPendingDueNo = useMemo(() => {
    const pending = (filteredPaymentData || []).filter(
      (r) => String(r.received || '').trim().toLowerCase() === 'pending'
    );
    return pending.length ? Math.min(...pending.map((r) => Number(r.dueNo))) : null;
  }, [filteredPaymentData]);

  const summaryData = useMemo(() => {
    if (!loanData?.paymentData) return [];
    const paymentData = loanData.paymentData;
    const totalPrincipal = parseFloat(loanData.totalLoanAmount) || 0;

    // BOTH column sets. An overdue instalment MOVES its figures (login.js:335-360):
    // principal/interest become 0 and the values land in arrearPrincipal/
    // arrearInterest. Summing only the base columns made eight of krish's 24
    // instalments contribute nothing, so a fully-paid Rs.3,00,000 loan reported
    // Rs.85,380 of interest instead of Rs.1,15,920.
    //
    // Adding them cannot double-count: a row carries at most ONE of
    // {total, arrearTotal}, enforced by the [DOUBLE-CHARGE GUARD] at
    // login.js:392-408, which halts collection for any row holding both.
    const totalInterest = paymentData.reduce(
      (sum, item) => sum + (item.interest || 0) + (item.arrearInterest || 0), 0);

    const pendingPrincipalOutstanding = paymentData
      .filter((item) => isPaymentPending(item.received))
      .reduce((sum, item) => sum + (item.principal || 0), 0);

    const pendingInterestOutstanding = paymentData
      .filter((item) => isPaymentPending(item.received))
      .reduce((sum, item) => sum + (item.interest || 0), 0);

    // OUTSTANDING means UNPAID — so these filter on status exactly like the two
    // EMI sums above. They did not, and a collected arrear row therefore kept
    // counting toward Outstanding Principal for ever.
    //
    // That missing filter is what drove emiCollection.js to ZERO the arrear
    // columns when an instalment was marked received: the row was destroyed to
    // protect this total. The row is the permanent record of what was owed and
    // paid, so the total is what had to change.
    const arrearPrincipalOutstanding = paymentData
      .filter((item) => isPaymentPending(item.received))
      .reduce((sum, item) => sum + (item.arrearPrincipal || 0), 0);

    const arrearInterestOutstanding = paymentData
      .filter((item) => isPaymentPending(item.received))
      .reduce((sum, item) => sum + (item.arrearInterest || 0), 0);

    const totalPrincipalOutstanding = pendingPrincipalOutstanding + arrearPrincipalOutstanding;
    const totalInterestOutstanding = pendingInterestOutstanding + arrearInterestOutstanding;

    // Both column sets here too, for the same reason as totalInterest above —
    // and this pair is where the contradiction showed on screen: krish's card
    // read "Collected Principal 2,00,000" beside "Principal Amount 3,00,000" on
    // a loan with all 24 instalments Received. The missing 1,00,000 was sitting
    // in arrearPrincipal on dues 17-24.
    //
    // The status filter stays exactly as it was. The defect was the missing
    // column, not the filter — an arrear row that has been collected IS
    // collected, and its figures are the permanent record of what was paid.
    //
    // A Foreclosed row's principal was collected too — the settlement clears it
    // (forecloseLoan.js). Its interest was waived, so Collected Interest stays on
    // Received rows only. Without this SEKAR's fully settled 50,000 loan read
    // "Collected Principal 40,000".
    const collectedPrincipal = paymentData
      .filter((item) => item.received === 'Received' || item.received === 'Foreclosed')
      .reduce((sum, item) => {
        const own = item.principal || 0;
        const arr = item.arrearPrincipal || 0;
        // Same single-amount rule forecloseLoan.js uses for a corrupt row with both.
        return sum + (item.received === 'Foreclosed' && own > 0 && arr > 0 ? Math.max(own, arr) : own + arr);
      }, 0);
    const collectedInterest = paymentData
      .filter((item) => item.received === 'Received')
      .reduce((sum, item) => sum + (item.interest || 0) + (item.arrearInterest || 0), 0);

    return [
      { label: 'Principal Amount', value: totalPrincipal, type: 'primary' },
      { label: 'Interest Amount', value: totalInterest, type: 'primary' },
      { label: 'Outstanding Principal', value: totalPrincipalOutstanding, type: 'warning' },
      { label: 'Outstanding Interest', value: totalInterestOutstanding, type: 'warning' },
      { label: 'Arrear Principal', value: arrearPrincipalOutstanding, type: 'danger' },
      { label: 'Arrear Interest', value: arrearInterestOutstanding, type: 'danger' },
      { label: 'Collected Principal', value: collectedPrincipal, type: 'success' },
      { label: 'Collected Interest', value: collectedInterest, type: 'success' },
    ];
  }, [loanData]);

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
    setTimeout(() => {
      setNotification(prev => ({ ...prev, open: false }));
    }, 5000);
  };

  const calculateTotalSettlement = (data) => {
    const principal = parseFloat(data.balancePrincipal) || 0;
    const charge = parseFloat(data.preclosureCharge) || 0;
    const gst = parseFloat(data.gst) || 0;
    const bounce = parseFloat(data.bounceCharges) || 0;
    const deposit = parseFloat(data.securityDeposit) || 0;
    return (principal + charge + gst + bounce - deposit).toFixed(2);
  };

  const handleForeclosureClick = async () => {
    if (!loanData) {
      showNotification('Please wait while loan data loads', 'warning');
      return;
    }

    const isAlreadyForeclosed =
      loanData.completionStatus === 'Foreclosed' ||
      loanData.loanDetails?.completionStatus === 'Foreclosed' ||
      loanData.foreclosureDate ||
      loanData.loanStatus === 'closed' ||
      loanData.loanStatus === 'foreclosed';

    if (isAlreadyForeclosed) {
      showNotification('This loan has already been foreclosed', 'info');
      return;
    }

    const pendingPayments = loanData.paymentData.filter(payment => isPaymentPending(payment.received));

    if (pendingPayments.length === 0) {
      showNotification('This loan has no pending payments to foreclose', 'info');
      return;
    }

    // ARREARS COUNT. An overdue instalment has its principal moved into
    // arrearPrincipal and `principal` set to 0, so summing `principal` alone
    // misses every overdue due. Member 2's dues 2-5 read principal 0 /
    // arrearPrincipal 4167: the payoff came to 79,173 against a real debt of
    // 95,833, which would have left 16,660 stranded on Loans Outstanding and
    // undercharged the 6% preclosure fee by about 1,000.
    //
    // total + arrearTotal is already how login.js and emiCollection.js bill an
    // instalment, so this is the established rule rather than a new one.
    //
    // A row carrying BOTH amounts is a known corruption — the arrear duplicates
    // the row's own EMI. forecloseLoan.js:53-63 takes the single larger amount
    // rather than the sum for exactly this reason; the same rule is applied here
    // so the screen and the server agree on the payoff.
    const dueOf = (own, arrear) => {
      const a = parseFloat(own || 0);
      const b = parseFloat(arrear || 0);
      return a > 0 && b > 0 ? Math.max(a, b) : a + b;
    };

    const balancePrincipal = pendingPayments.reduce(
      (sum, payment) => sum + dueOf(payment.principal, payment.arrearPrincipal), 0);
    const balanceInterest = pendingPayments.reduce(
      (sum, payment) => sum + dueOf(payment.interest, payment.arrearInterest), 0);
    const totalBalanceAmount = pendingPayments.reduce(
      (sum, payment) => sum + dueOf(payment.total, payment.arrearTotal), 0);

    const preclosureRate = 6;
    const preclosureCharge = (balancePrincipal * preclosureRate / 100).toFixed(2);
    const securityDeposit = parseFloat(loanData.securityDeposit || 0);

    const today = new Date();
    const formattedToday = today.toISOString().split('T')[0];
    const defaultGstPercentage = 8;
    const gstAmount = parseFloat((parseFloat(preclosureCharge) * defaultGstPercentage / 100).toFixed(2));

    const initialForeclosureData = {
      balancePrincipal: balancePrincipal.toFixed(2),
      balanceInterest: balanceInterest.toFixed(2),
      totalBalanceAmount: totalBalanceAmount.toFixed(2),
      preclosureRate,
      preclosureCharge,
      gstType: 'percentage',
      gstPercentage: defaultGstPercentage,
      gst: gstAmount,
      bounceCharges: 0,
      securityDeposit: securityDeposit.toFixed(2),
      totalAmount: 0,
      pendingCount: pendingPayments.length,
      narration: 'Loan Foreclosure - Full Settlement of All Pending EMIs',
      paymentDate: formattedToday
    };

    initialForeclosureData.totalAmount = calculateTotalSettlement(initialForeclosureData);

    setForeclosureData(initialForeclosureData);

    if (selectedOption?.customerUniqueId) {
      setFetchingClosingBalance(true);
      setMemberClosingBalance(null);
      try {
        const balRes = await axios.get(
          `${API_BASE_URL}/api/v2/member-closing-balances?customerUniqueIds=${encodeURIComponent(selectedOption.customerUniqueId)}`,
          { headers: { 'Authorization': 'Bearer ' + loanJwt } }
        );
        const balMap = balRes.data?.data || {};
        const balance = Number(balMap[selectedOption.customerUniqueId] || 0);
        setMemberClosingBalance(balance);
      } catch (balErr) {
        console.error('Error fetching closing balance:', balErr);
        setMemberClosingBalance(null);
      } finally {
        setFetchingClosingBalance(false);
      }
    }

    setOpenForeclosureModal(true);
  };

  const handleForeclosureRateChange = (e) => {
    const rate = parseFloat(e.target.value) || 0;
    const balancePrincipal = parseFloat(foreclosureData.balancePrincipal);
    const charge = (balancePrincipal * rate / 100).toFixed(2);

    const updated = {
      ...foreclosureData,
      preclosureRate: rate,
      preclosureCharge: charge,
    };
    if (updated.gstType === 'percentage') {
      updated.gst = parseFloat((parseFloat(charge) * (updated.gstPercentage || 0) / 100).toFixed(2));
    }
    updated.totalAmount = calculateTotalSettlement(updated);
    setForeclosureData(updated);
  };

  const handleForeclosureChargeChange = (e) => {
    const charge = parseFloat(e.target.value) || 0;
    const updated = {
      ...foreclosureData,
      preclosureCharge: charge.toFixed(2),
    };
    if (updated.gstType === 'percentage') {
      updated.gst = parseFloat((charge * (updated.gstPercentage || 0) / 100).toFixed(2));
    }
    updated.totalAmount = calculateTotalSettlement(updated);
    setForeclosureData(updated);
  };

  const handleGstTypeChange = (newType) => {
    const charge = parseFloat(foreclosureData.preclosureCharge) || 0;
    let gstVal = 0;
    const gstPct = foreclosureData.gstPercentage || 8;

    if (newType === 'percentage') {
      gstVal = parseFloat((charge * gstPct / 100).toFixed(2));
    } else {
      gstVal = parseFloat(foreclosureData.gst) || 0;
    }

    const updated = {
      ...foreclosureData,
      gstType: newType,
      gst: gstVal,
    };
    updated.totalAmount = calculateTotalSettlement(updated);
    setForeclosureData(updated);
  };

  const handleGstChange = (e) => {
    const val = parseFloat(e.target.value) || 0;
    const charge = parseFloat(foreclosureData.preclosureCharge) || 0;

    let updated;
    if (foreclosureData.gstType === 'percentage') {
      const gstAmount = parseFloat((charge * val / 100).toFixed(2));
      updated = { ...foreclosureData, gstPercentage: val, gst: gstAmount };
    } else {
      updated = { ...foreclosureData, gst: val };
    }
    updated.totalAmount = calculateTotalSettlement(updated);
    setForeclosureData(updated);
  };

  const handleBounceChargesChange = (e) => {
    const bounceVal = parseFloat(e.target.value) || 0;
    const updated = {
      ...foreclosureData,
      bounceCharges: bounceVal,
    };
    updated.totalAmount = calculateTotalSettlement(updated);
    setForeclosureData(updated);
  };

  const handleForeclosureInputChange = (field, value) => {
    setForeclosureData({
      ...foreclosureData,
      [field]: value
    });
  };

  const handleConfirmForeclosure = () => {
    const settlementAmt = parseFloat(foreclosureData.totalAmount) || 0;
    const availableBalance = memberClosingBalance !== null ? memberClosingBalance : Infinity;
    if (memberClosingBalance !== null && availableBalance < settlementAmt) {
      showNotification(
        `Insufficient account balance! Member's closing balance is ₹${availableBalance.toFixed(2)} but the settlement amount is ₹${settlementAmt.toFixed(2)}. Foreclosure is not allowed.`,
        'error'
      );
      return;
    }
    setShowForeclosureConfirm(true);
  };

  const { data: userData, roleName: loanRoleName, jwt: loanJwt } = useAuth();
  const isLoanAdmin = String(loanRoleName || '').toLowerCase().trim() === 'superadmin';

  const handleProcessForeclosure = async () => {
    setShowForeclosureConfirm(false);
    setProcessingForeclosure(true);

    try {
      if (!loanData?.paymentData?.length) {
        throw new Error('No loan data available');
      }

      const pendingPayments = loanData.paymentData.filter(p => isPaymentPending(p.received));
      if (!pendingPayments.length) {
        throw new Error('No pending payments');
      }

      const memberId = pendingPayments[0].memberId || selectedOption?.memberId;
      const rawDate = foreclosureData.paymentDate || new Date().toISOString().split('T')[0];
      const formattedDate = rawDate + ' ' + new Date().toLocaleTimeString();
      const displayDate = `${formatDate(rawDate)} ${new Date().toLocaleTimeString()}`;

      const totalAmount = parseFloat(foreclosureData.totalAmount);
      const userBranchId = userData?.branchId;

      const payload = {
        memberId,
        totalAmount,
        paymentDate: formattedDate,
        narration: foreclosureData.narration || "Loan Foreclosure",
        customerUniqueId: selectedOption?.customerUniqueId || pendingPayments[0]?.customerUniqueId,
        accountNumber: Array.isArray(selectedOption?.accountNumber)
          ? selectedOption?.accountNumber[0]
          : selectedOption?.accountNumber || "N/A",
        branchId: userBranchId,
        gst: parseFloat(foreclosureData.gst) || 0,
        bounceCharges: parseFloat(foreclosureData.bounceCharges) || 0,
        securityDeposit: parseFloat(foreclosureData.securityDeposit) || 0,
        preclosureRate: parseFloat(foreclosureData.preclosureRate) || 0,
        preclosureCharge: parseFloat(foreclosureData.preclosureCharge) || 0
      };

      await axios.post(`${API_BASE_URL}/api/v2/foreclose-loan`, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + loanJwt
        }
      });

      let smsPayload = { phoneNo: "", message: "" };

      if (selectedOption?.mobileNumber) {
        smsPayload = {
          phoneNo: selectedOption.mobileNumber,
          message: `Your loan account ${selectedOption.name || selectedOption.customerUniqueId} is foreclosed with payment of ${totalAmount.toFixed(2)}. Thank you for your business. JLNLTD`
        };

        try {
          await apiCall(
            `${API_BASE_URL}/api/debitsms`,
            'post',
            smsPayload,
            {
              headers: { "Content-Type": "application/json" }
            }
          );
        } catch (smsError) {
          console.error("Foreclosure SMS sending failed:", smsError);
        }
      }

      // Mirror forecloseLoan.js: each row takes its principal, the last pending
      // row also the rest of the settlement (charges net of deposit). Interest
      // and penalty are waived, so they are not shown as received.
      const principalOf = (p) => {
        const own = Number(p.principal) || 0;
        const arr = Number(p.arrearPrincipal) || 0;
        return own > 0 && arr > 0 ? Math.max(own, arr) : own + arr;
      };
      const pendingRows = loanData.paymentData.filter(p => p.received === "Pending");
      const lastPendingDueNo = pendingRows.length
        ? Math.max(...pendingRows.map(p => Number(p.dueNo)))
        : null;
      const settlementRemainder =
        Math.round((totalAmount - pendingRows.reduce((s, p) => s + principalOf(p), 0)) * 100) / 100;

      const updatedPaymentData = loanData.paymentData.map(p => {
        if (p.received === "Pending") {
          return {
            ...p,
            received: "Foreclosed",
            paymentDate: displayDate,
            emiReceived: principalOf(p) + (Number(p.dueNo) === lastPendingDueNo ? settlementRemainder : 0),
            penaltyPaid: 0,
          };
        }
        return p;
      });

      const updatedData = {
        ...loanData,
        paymentData: updatedPaymentData,
        loanStatus: "foreclosed",
        isForeclosed: true,
        isLoanClosed: true
      };

      if (setLoanData && typeof setLoanData === 'function') {
        setLoanData(updatedData);
      }

      setLocalLoanData(updatedData);

      setTimeout(async () => {
        try {
          const refreshedData = await getLoanDetails(selectedOption);
          if (refreshedData) {
            const prevIndex = loanData?.currentLoanIndex || 0;
            const selectedLoan = refreshedData.loans[prevIndex] || refreshedData.loans[0];
            const updatedWithIndex = {
              ...refreshedData,
              ...selectedLoan,
              currentLoanIndex: prevIndex
            };
            setLocalLoanData(updatedWithIndex);
            setLoanData(updatedWithIndex);
            setForceRefresh((prev) => prev + 1);
          }
        } catch (refreshError) {
          console.error("Error refreshing after foreclosure:", refreshError);
        }
      }, 1000);

      showNotification('Foreclosure processed successfully!', 'success');
      setOpenForeclosureModal(false);
    } catch (error) {
      console.error('Foreclosure failed:', error);
      showNotification(`Foreclosure failed: ${error.message}`, 'error');
    } finally {
      setProcessingForeclosure(false);
    }
  };

  const handlePayLoanClick = (paymentItem) => {
    setSelectedPayment(paymentItem);
    setOpenConfirmModal(true);
  };

  const handlePayLoan = async () => {
    if (!selectedPayment) return;
    setProcessingPayment(true);
    setOpenConfirmModal(false);

    try {
      if (!selectedPayment.id) {
        throw new Error('Payment ID is missing');
      }

      const hasArrears = Number(selectedPayment.arrearTotal || 0) > 0;
      const baseAmount = hasArrears
        ? Number(selectedPayment.arrearTotal || 0)
        : Number(selectedPayment.total || 0);
      const totalAmount = baseAmount + Number(selectedPayment.penalty || 0);

      // ONE call. The server writes the passbook row, the ledger legs and the
      // "Received" mark inside a single database transaction
      // (services/emiCollection.js collectStatement), so they cannot come apart.
      //
      // This replaced a two-call flow that posted the debit to /api/addDetails
      // FIRST and then sent an /addDetails-shaped body ({model, type, id, …}) to
      // this endpoint. The endpoint reads {loanStatementId, customerUniqueId, …}
      // and rejected it with 400 "loanStatementId is required" — after the money
      // had already left. loka (JL002000008) lost ₹12,333 that way: debited, row
      // still Pending, then billed again as arrears by the overdue sweep.
      //
      // collectEmi.js:9-13 documents this exact defect as the reason the v2
      // endpoint was written; the button had never been switched over to it.
      const response = await fetch(
        `${API_BASE_URL}/api/v2/collect-emi`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${loanJwt || ''}`,
          },
          body: JSON.stringify({
            loanStatementId: selectedPayment.id,
            customerUniqueId:
              selectedOption?.customerUniqueId || selectedPayment?.customerUniqueId,
            accountNumber: Array.isArray(selectedOption?.accountNumber)
              ? selectedOption.accountNumber[0]
              : selectedOption?.accountNumber || 'N/A',
            // The logged-in user's branch, matching how the foreclosure call
            // above resolves it (userData from useAuth at :887). The server
            // needs it to pick the branch's ledger accounts; without it
            // collectStatement skips the ledger legs rather than failing, and
            // check_emi_parity.js would report the gap afterwards.
            branchId: selectedOption?.branchId ?? userData?.branchId ?? null,
            paymentDate: new Date().toISOString(),
          }),
        }
      );

      const responseData = await response.json();
      console.log("API response:", responseData);

      if (!response.ok) {
        // The endpoint returns a typed `reason` with its own message
        // (insufficient_balance, future_due, nothing_due, not_pending); show
        // that rather than a generic failure, so the operator knows what to do.
        throw new Error(responseData.error || 'Failed to update payment status');
      }

      // What the SERVER actually collected. It derives the figure itself —
      // arrearTotal when arrears exist, else total, plus penalty
      // (emiCollection.js:324-337) — and that is the single definition. The
      // local `totalAmount` is kept only for the confirmation dialog's text.
      const collectedAmount = Number(responseData.amount ?? totalAmount);

      showNotification('Payment marked as received successfully!', 'success');

      if (loanData && loanData.paymentData) {
        const updatedPaymentData = loanData.paymentData.map(item => {
          if (item.id === selectedPayment.id) {
            return {
              ...item,
              received: 'Received',
              paymentDate: `${formatDate(new Date().toISOString())} ${new Date().toLocaleTimeString()}`,
              // The server's figure, not the client's guess. Reached only after
              // response.ok, so this can no longer paint a row "Received" that
              // the server refused.
              emiReceived: collectedAmount,
              penaltyPaid: Number(selectedPayment.penalty || 0),
              // Settled arrears are cleared server-side; mirror that so the row
              // does not keep showing a due it has just paid.
              ...(Number(item.arrearTotal || 0) > 0
                ? { arrearPrincipal: 0, arrearInterest: 0, arrearTotal: 0 }
                : {}),
            };
          }
          return item;
        });

        const updatedLoanData = {
          ...loanData,
          paymentData: updatedPaymentData
        };

        setLocalLoanData(null);
        setForceRefresh(prev => prev + 1);

        setTimeout(() => {
          setLocalLoanData(updatedLoanData);
          setLoanData(updatedLoanData);
          setForceRefresh(prev => prev + 1);
        }, 50);
      }

      setTimeout(async () => {
        try {
          const updatedData = await getLoanDetails(selectedOption);

          if (updatedData) {
            const prevIndex = loanData?.currentLoanIndex || 0;
            const selectedLoan = updatedData.loans[prevIndex] || updatedData.loans[0];
            const updatedWithIndex = {
              ...updatedData,
              ...selectedLoan,
              currentLoanIndex: prevIndex
            };
            setLocalLoanData(updatedWithIndex);
            setLoanData(updatedWithIndex);
            setForceRefresh(prev => prev + 1);
            setSelectedPayment(null);
          }
        } catch (refreshError) {
          console.error("Error fetching fresh data:", refreshError);
        } finally {
          setProcessingPayment(false);
        }
      }, 1000);
    } catch (error) {
      console.error('Error updating payment status:', error);
      showNotification(
        error.message || 'Failed to update payment status. Please try again.',
        'error'
      );
      setProcessingPayment(false);
    }
  };

  const handleEditLoanStatement = (item) => {
    setEditLoanData({
      id: item.id,
      principal: item.principal || 0,
      interest: item.interest || 0,
      total: item.total || 0,
      arrearPrincipal: item.arrearPrincipal || 0,
      arrearInterest: item.arrearInterest || 0,
      arrearTotal: item.arrearTotal || 0,
      received: item.received || 'Pending',
      penalty: item.penalty || 0,
      paid: item.paid || 0,
      emiReceived: item.emiReceived || 0,
      outstanding: item.outstanding || 0,
    });
    setEditLoanModalOpen(true);
  };

  const handleEditLoanFieldChange = (field, value) => {
    setEditLoanData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'principal' || field === 'interest') {
        updated.total = (parseFloat(updated.principal) || 0) + (parseFloat(updated.interest) || 0);
      }
      if (field === 'arrearPrincipal' || field === 'arrearInterest') {
        updated.arrearTotal = (parseFloat(updated.arrearPrincipal) || 0) + (parseFloat(updated.arrearInterest) || 0);
      }
      return updated;
    });
  };

  const handleSaveLoanEdit = async () => {
    if (!editLoanData?.id) return;
    setSavingLoanEdit(true);
    try {
      const payload = {
        model: 'loanstatement',
        type: 'Edit',
        id: editLoanData.id,
        principal: parseFloat(editLoanData.principal) || 0,
        interest: parseFloat(editLoanData.interest) || 0,
        total: parseFloat(editLoanData.total) || 0,
        arrearPrincipal: parseFloat(editLoanData.arrearPrincipal) || 0,
        arrearInterest: parseFloat(editLoanData.arrearInterest) || 0,
        arrearTotal: parseFloat(editLoanData.arrearTotal) || 0,
        received: editLoanData.received,
        penalty: parseFloat(editLoanData.penalty) || 0,
        penaltyPaid: parseFloat(editLoanData.paid) || 0,
        emiReceived: parseFloat(editLoanData.emiReceived) || 0,
        outstanding: parseFloat(editLoanData.outstanding) || 0,
      };

      await axios.post(`${API_BASE_URL}/api/addDetails`, payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + loanJwt,
        },
      });

      showNotification('Loan statement updated successfully!', 'success');
      setEditLoanModalOpen(false);
      setEditLoanData(null);

      const refreshedData = await getLoanDetails(selectedOption);
      if (refreshedData) {
        const prevIndex = loanData?.currentLoanIndex || 0;
        const selectedLoan = refreshedData.loans[prevIndex] || refreshedData.loans[0];
        const updatedWithIndex = {
          ...refreshedData,
          ...selectedLoan,
          currentLoanIndex: prevIndex
        };
        setLocalLoanData(updatedWithIndex);
        setLoanData(updatedWithIndex);
        setForceRefresh((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Error updating loan statement:', error);
      showNotification(`Failed to update: ${error.message}`, 'error');
    } finally {
      setSavingLoanEdit(false);
    }
  };

  const handleDeleteLoanStatement = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.post(
            `${API_BASE_URL}/api/delDetails`,
            { model: 'loanstatement', id },
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + loanJwt,
              },
            }
          );

          Swal.fire("Deleted!", "Loan statement row has been deleted.", "success");

          const refreshedData = await getLoanDetails(selectedOption);
          if (refreshedData) {
            const prevIndex = loanData?.currentLoanIndex || 0;
            const selectedLoan = refreshedData.loans[prevIndex] || refreshedData.loans[0];
            const updatedWithIndex = {
              ...refreshedData,
              ...selectedLoan,
              currentLoanIndex: prevIndex
            };
            setLocalLoanData(updatedWithIndex);
            setLoanData(updatedWithIndex);
            setForceRefresh((prev) => prev + 1);
          }
        } catch (error) {
          console.error('Error deleting loan statement:', error);
          Swal.fire("Error!", `Failed to delete: ${error.message}`, "error");
        }
      }
    });
  };

  const handleAddLoanRowOpen = () => {
    setAddLoanData({
      memberId: loanData?.memberId || selectedOption?.memberId,
      dueNo: (loanData?.paymentData?.length || 0) + 1,
      dueDate: new Date().toISOString().split('T')[0],
      principal: 0,
      interest: 0,
      total: 0,
      arrearPrincipal: 0,
      arrearInterest: 0,
      arrearTotal: 0,
      received: 'Pending',
      penalty: 0,
      penaltyPaid: 0,
      emiReceived: 0,
      outstanding: 0,
    });
    setAddLoanModalOpen(true);
  };

  const handleAddLoanFieldChange = (field, value) => {
    setAddLoanData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'principal' || field === 'interest') {
        updated.total = (parseFloat(updated.principal) || 0) + (parseFloat(updated.interest) || 0);
      }
      if (field === 'arrearPrincipal' || field === 'arrearInterest') {
        updated.arrearTotal = (parseFloat(updated.arrearPrincipal) || 0) + (parseFloat(updated.arrearInterest) || 0);
      }
      return updated;
    });
  };

  const handleSaveNewLoanRow = async () => {
    if (!addLoanData.memberId) {
      showNotification('Member ID is missing. Cannot add row.', 'error');
      return;
    }
    setSavingNewLoan(true);
    try {
      const payload = {
        model: 'loanstatement',
        type: 'Add',
        ...addLoanData,
        principal: parseFloat(addLoanData.principal) || 0,
        interest: parseFloat(addLoanData.interest) || 0,
        total: parseFloat(addLoanData.total) || 0,
        arrearPrincipal: parseFloat(addLoanData.arrearPrincipal) || 0,
        arrearInterest: parseFloat(addLoanData.arrearInterest) || 0,
        arrearTotal: parseFloat(addLoanData.arrearTotal) || 0,
        penalty: parseFloat(addLoanData.penalty) || 0,
        penaltyPaid: parseFloat(addLoanData.penaltyPaid) || 0,
        emiReceived: parseFloat(addLoanData.emiReceived) || 0,
        outstanding: parseFloat(addLoanData.outstanding) || 0,
      };

      await axios.post(`${API_BASE_URL}/api/addDetails`, payload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + loanJwt,
        },
      });

      showNotification('New loan row added successfully!', 'success');
      setAddLoanModalOpen(false);

      const refreshedData = await getLoanDetails(selectedOption);
      if (refreshedData) {
        const prevIndex = loanData?.currentLoanIndex || 0;
        const selectedLoan = refreshedData.loans[prevIndex] || refreshedData.loans[0];
        const updatedWithIndex = {
          ...refreshedData,
          ...selectedLoan,
          currentLoanIndex: prevIndex
        };
        setLocalLoanData(updatedWithIndex);
        setLoanData(updatedWithIndex);
        setForceRefresh((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Error adding loan row:', error);
      showNotification(`Failed to add: ${error.message}`, 'error');
    } finally {
      setSavingNewLoan(false);
    }
  };

  const handleDeleteAllLoanStatements = () => {
    const memberId = loanData?.memberId || selectedOption?.memberId;
    if (!memberId) {
      Swal.fire("Error!", "Member ID not found.", "error");
      return;
    }

    const idsToDelete = loanData.paymentData?.map(p => p.id) || [];
    if (idsToDelete.length === 0) {
      Swal.fire("Info", "No rows to delete.", "info");
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: `You want to delete ALL ${idsToDelete.length} loan statement rows for this member? You won't be able to revert this!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete all!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Deleting...',
          html: 'Please wait while all rows are being deleted.',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        try {
          for (const id of idsToDelete) {
            await axios.post(
              `${API_BASE_URL}/api/delDetails`,
              { model: 'loanstatement', id },
              {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: 'Bearer ' + loanJwt,
                },
              }
            );
          }

          Swal.fire("Deleted!", "All rows have been deleted successfully.", "success");

          const refreshedData = await getLoanDetails(selectedOption);
          if (refreshedData) {
            const prevIndex = loanData?.currentLoanIndex || 0;
            const selectedLoan = refreshedData.loans[prevIndex] || refreshedData.loans[0];
            const updatedWithIndex = {
              ...refreshedData,
              ...selectedLoan,
              currentLoanIndex: prevIndex
            };
            setLocalLoanData(updatedWithIndex);
            setLoanData(updatedWithIndex);
            setForceRefresh((prev) => prev + 1);
          } else {
            setLocalLoanData(null);
            setLoanData(null);
          }
        } catch (error) {
          console.error('Error deleting all rows:', error);
          Swal.fire("Error!", `Failed to delete all: ${error.message}`, "error");
        }
      }
    });
  };

  return (
    <div className="mt-4">
      {/* Toast-style notification banner */}
      {notification.open && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg border text-sm transition-all duration-300 ${notification.severity === 'error' ? 'bg-danger-50 text-danger-800 border-danger-200' :
          notification.severity === 'warning' ? 'bg-warning-50 text-warning-800 border-warning-200' :
            notification.severity === 'info' ? 'bg-primary-50 text-primary-800 border-primary-200' :
              'bg-success-50 text-success-800 border-success-200'
          }`}>
          {notification.severity === 'error' && <FaExclamationTriangle />}
          {notification.severity === 'warning' && <FaExclamationTriangle className="text-warning-500" />}
          <span>{notification.message}</span>
          <button onClick={() => setNotification({ ...notification, open: false })} className="ml-2 hover:opacity-80">
            <FaTimes />
          </button>
        </div>
      )}

      {/* Foreclosure Modal */}
      <Modal
        isOpen={openForeclosureModal}
        onClose={() => setOpenForeclosureModal(false)}
        title="Loan Foreclosure"
        size="lg"
      >
        <div className="space-y-4">
          <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
            <p className="text-sm font-semibold text-slate-700">Member Name: <span className="text-slate-900 font-bold">{selectedOption?.name || 'N/A'}</span></p>

            {fetchingClosingBalance ? (
              <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-500">
                <Spinner size="sm" />
                <span>Fetching account balance...</span>
              </div>
            ) : memberClosingBalance !== null ? (
              <div className={`mt-2 p-2 rounded border text-xs flex items-center justify-between ${memberClosingBalance < parseFloat(foreclosureData.totalAmount || 0)
                ? 'bg-amber-50 border-amber-200 text-amber-800'
                : 'bg-emerald-50 border-emerald-200 text-emerald-800'
                }`}>
                <span>Account Closing Balance: <strong>₹{memberClosingBalance.toFixed(2)}</strong></span>
                {memberClosingBalance < parseFloat(foreclosureData.totalAmount || 0) && (
                  <span className="font-bold text-danger-600">⚠️ Insufficient balance! Cannot foreclose.</span>
                )}
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Balance Principal (₹)</label>
              <input type="text" className="w-full border border-slate-300 rounded-lg p-2 bg-slate-100 font-medium" value={foreclosureData.balancePrincipal} readOnly />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Preclosure Rate (%)</label>
              <input type="number" min="0" step="0.1" className="w-full border border-slate-300 rounded-lg p-2" value={foreclosureData.preclosureRate} onChange={handleForeclosureRateChange} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Preclosure Charge (₹)</label>
              <input type="number" min="0" step="0.01" className="w-full border border-slate-300 rounded-lg p-2" value={foreclosureData.preclosureCharge} onChange={handleForeclosureChargeChange} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">GST Type</label>
              <div className="flex gap-4 p-2">
                <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="radio" checked={foreclosureData.gstType === 'percentage'} onChange={() => handleGstTypeChange('percentage')} />
                  <span>Percentage (%)</span>
                </label>
                <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="radio" checked={foreclosureData.gstType === 'amount'} onChange={() => handleGstTypeChange('amount')} />
                  <span>Amount (₹)</span>
                </label>
              </div>
            </div>

            {foreclosureData.gstType === 'percentage' ? (
              <>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">GST Percentage (%)</label>
                  <input type="number" min="0" step="0.1" className="w-full border border-slate-300 rounded-lg p-2" value={foreclosureData.gstPercentage} onChange={handleGstChange} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">GST Amount (₹) [Auto]</label>
                  <input type="text" className="w-full border border-slate-300 rounded-lg p-2 bg-slate-100 font-bold text-[#0d9488]" value={parseFloat(foreclosureData.gst || 0).toFixed(2)} readOnly />
                </div>
              </>
            ) : (
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">GST Amount (₹)</label>
                <input type="number" min="0" step="0.01" className="w-full border border-slate-300 rounded-lg p-2" value={foreclosureData.gst} onChange={handleGstChange} />
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Bounce Charges (₹)</label>
              <input type="number" min="0" step="0.01" className="w-full border border-slate-300 rounded-lg p-2" value={foreclosureData.bounceCharges} onChange={handleBounceChargesChange} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Security Deposit (₹) [Deducted]</label>
              <input type="text" className="w-full border border-slate-300 rounded-lg p-2 bg-slate-100 font-medium" value={foreclosureData.securityDeposit} readOnly />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Settlement Amount (₹)</label>
              <input type="text" className="w-full border border-slate-300 rounded-lg p-2 bg-slate-100 font-bold text-primary-700" value={foreclosureData.totalAmount} readOnly />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Payment Date</label>
              <input type="date" className="w-full border border-slate-300 rounded-lg p-2 bg-slate-100" value={foreclosureData.paymentDate} readOnly />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-3">
          <Button variant="secondary" onClick={() => setOpenForeclosureModal(false)}>Close</Button>
          <Button
            variant="primary"
            onClick={handleConfirmForeclosure}
            disabled={
              processingForeclosure ||
              fetchingClosingBalance ||
              (memberClosingBalance !== null && memberClosingBalance < parseFloat(foreclosureData.totalAmount || 0))
            }
          >
            {fetchingClosingBalance ? <Spinner size="sm" /> : 'Process Foreclosure'}
          </Button>
        </div>
      </Modal>

      {/* Foreclosure Confirmation Modal */}
      <Modal
        isOpen={showForeclosureConfirm}
        onClose={() => setShowForeclosureConfirm(false)}
        title="Confirm Loan Foreclosure"
        size="md"
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">Are you sure you want to foreclose this loan? This action cannot be undone.</p>
          <div className="bg-slate-50 p-3 rounded-lg border text-sm space-y-1.5 font-medium">
            <div className="flex justify-between"><span>Balance Principal:</span> <span>₹{parseFloat(foreclosureData.balancePrincipal).toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Preclosure Charge:</span> <span>₹{parseFloat(foreclosureData.preclosureCharge).toFixed(2)}</span></div>
            <div className="flex justify-between"><span>GST:</span> <span>₹{parseFloat(foreclosureData.gst || 0).toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Bounce Charges:</span> <span>₹{parseFloat(foreclosureData.bounceCharges || 0).toFixed(2)}</span></div>
            <div className="flex justify-between text-danger-600"><span>Security Deposit (Deducted):</span> <span>-₹{parseFloat(foreclosureData.securityDeposit || 0).toFixed(2)}</span></div>
            <div className="border-t pt-1.5 flex justify-between font-bold text-base text-primary-700"><span>Total Settlement Amount:</span> <span>₹{parseFloat(foreclosureData.totalAmount).toFixed(2)}</span></div>
          </div>
          {memberClosingBalance !== null && (
            <p className={`text-xs font-bold ${memberClosingBalance >= parseFloat(foreclosureData.totalAmount || 0) ? 'text-success-600' : 'text-danger-600'}`}>
              Member Closing Balance: ₹{memberClosingBalance.toFixed(2)} {memberClosingBalance >= parseFloat(foreclosureData.totalAmount || 0) ? ' (Sufficient)' : ' (Insufficient)'}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="secondary" onClick={() => setShowForeclosureConfirm(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleProcessForeclosure} disabled={processingForeclosure}>
            {processingForeclosure ? <Spinner size="sm" /> : 'Confirm Foreclosure'}
          </Button>
        </div>
      </Modal>

      {/* Payment Confirmation Modal */}
      <Modal
        isOpen={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
        title="Confirm Payment Received"
        size="md"
      >
        {selectedPayment && (
          <div className="space-y-4">
            <p className="text-sm font-semibold text-slate-700">Installment #{selectedPayment.dueNo} - Due Date: {selectedPayment.dueDate}</p>

            {!(Number(selectedPayment.arrearTotal || 0) > 0) && (
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 text-sm space-y-1">
                <p className="font-bold text-primary-700 mb-1">EMI Details</p>
                <div className="flex justify-between"><span>Principal:</span> <span>{formatCurrency(selectedPayment.principal)}</span></div>
                <div className="flex justify-between"><span>Interest:</span> <span>{formatCurrency(selectedPayment.interest)}</span></div>
                <div className="flex justify-between font-bold text-primary-800 border-t pt-1 mt-1"><span>EMI Total:</span> <span>{formatCurrency(selectedPayment.total)}</span></div>
              </div>
            )}

            {(selectedPayment.arrearTotal > 0 || selectedPayment.arrearPrincipal > 0 || selectedPayment.arrearInterest > 0) && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm space-y-1">
                <p className="font-bold text-amber-700 mb-1">Arrear Details (Moved to Arrears)</p>
                <div className="flex justify-between"><span>Arrear Principal:</span> <span>{formatCurrency(selectedPayment.arrearPrincipal || 0)}</span></div>
                <div className="flex justify-between"><span>Arrear Interest:</span> <span>{formatCurrency(selectedPayment.arrearInterest || 0)}</span></div>
                <div className="flex justify-between font-bold text-amber-800 border-t pt-1 mt-1"><span>Arrear Total:</span> <span>{formatCurrency(selectedPayment.arrearTotal || 0)}</span></div>
              </div>
            )}

            {selectedPayment.penalty > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm flex justify-between font-bold text-purple-700">
                <span>Penalty:</span> <span>{formatCurrency(selectedPayment.penalty || 0)}</span>
              </div>
            )}

            <div className="bg-success-50 border border-success-200 rounded-lg p-3 text-base flex justify-between font-bold text-success-700">
              <span>Total Amount Received:</span>
              <span>
                {formatCurrency(
                  (Number(selectedPayment.arrearTotal || 0) > 0
                    ? Number(selectedPayment.arrearTotal || 0)
                    : Number(selectedPayment.total || 0)
                  ) + Number(selectedPayment.penalty || 0)
                )}
              </span>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="secondary" onClick={() => setOpenConfirmModal(false)} disabled={processingPayment}>Cancel</Button>
          <Button variant="primary" onClick={handlePayLoan} disabled={processingPayment}>
            {processingPayment ? <Spinner size="sm" /> : 'Confirm Received'}
          </Button>
        </div>
      </Modal>

      {/* ADMIN: Edit Loan Statement Row Modal */}
      <Modal
        isOpen={editLoanModalOpen}
        onClose={() => setEditLoanModalOpen(false)}
        title="Edit Loan Statement Row"
        size="lg"
      >
        {editLoanData && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Principal</label>
              <input type="number" min="0" step="0.01" className="w-full border border-slate-300 rounded-lg p-2" value={editLoanData.principal} onChange={(e) => handleEditLoanFieldChange('principal', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Interest</label>
              <input type="number" min="0" step="0.01" className="w-full border border-slate-300 rounded-lg p-2" value={editLoanData.interest} onChange={(e) => handleEditLoanFieldChange('interest', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Total (Auto)</label>
              <input type="number" min="0" step="0.01" className="w-full border border-slate-300 rounded-lg p-2 bg-slate-100" value={editLoanData.total} readOnly />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Arrear Principal</label>
              <input type="number" min="0" step="0.01" className="w-full border border-slate-300 rounded-lg p-2" value={editLoanData.arrearPrincipal} onChange={(e) => handleEditLoanFieldChange('arrearPrincipal', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Arrear Interest</label>
              <input type="number" min="0" step="0.01" className="w-full border border-slate-300 rounded-lg p-2" value={editLoanData.arrearInterest} onChange={(e) => handleEditLoanFieldChange('arrearInterest', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Arrear Total (Auto)</label>
              <input type="number" min="0" step="0.01" className="w-full border border-slate-300 rounded-lg p-2 bg-slate-100" value={editLoanData.arrearTotal} readOnly />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Received Status</label>
              <select className="w-full border border-slate-300 rounded-lg p-2 bg-white" value={editLoanData.received} onChange={(e) => handleEditLoanFieldChange('received', e.target.value)}>
                <option value="Received">Received</option>
                <option value="Pending">Pending</option>
                <option value="Foreclosed">Foreclosed</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Penalty</label>
              <input type="number" min="0" step="0.01" className="w-full border border-slate-300 rounded-lg p-2" value={editLoanData.penalty} onChange={(e) => handleEditLoanFieldChange('penalty', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Penalty Paid</label>
              <input type="number" min="0" step="0.01" className="w-full border border-slate-300 rounded-lg p-2" value={editLoanData.paid} onChange={(e) => handleEditLoanFieldChange('paid', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">EMI Received</label>
              <input type="number" min="0" step="0.01" className="w-full border border-slate-300 rounded-lg p-2" value={editLoanData.emiReceived} onChange={(e) => handleEditLoanFieldChange('emiReceived', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Outstanding</label>
              <input type="number" min="0" step="0.01" className="w-full border border-slate-300 rounded-lg p-2" value={editLoanData.outstanding} onChange={(e) => handleEditLoanFieldChange('outstanding', e.target.value)} />
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
          <Button variant="secondary" onClick={() => setEditLoanModalOpen(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveLoanEdit} disabled={savingLoanEdit}>
            {savingLoanEdit ? <Spinner size="sm" /> : 'Save Changes'}
          </Button>
        </div>
      </Modal>

      {/* ADMIN: Create New Loan Statement Row Modal */}
      <Modal
        isOpen={addLoanModalOpen}
        onClose={() => setAddLoanModalOpen(false)}
        title="Create New Loan Statement Row"
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Due No</label>
              <input type="number" className="w-full border border-slate-300 rounded-lg p-2" value={addLoanData.dueNo} onChange={(e) => handleAddLoanFieldChange('dueNo', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Due Date</label>
              <DatePicker
                selected={addLoanData.dueDate}
                onChange={(dateStr) => handleAddLoanFieldChange('dueDate', dateStr)}
                className="w-full border border-slate-300 rounded-lg p-2"
                placeholder="DD-MM-YYYY"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Status</label>
              <select className="w-full border border-slate-300 rounded-lg p-2 bg-white" value={addLoanData.received} onChange={(e) => handleAddLoanFieldChange('received', e.target.value)}>
                <option value="Pending">Pending</option>
                <option value="Received">Received</option>
                <option value="Foreclosed">Foreclosed</option>
              </select>
            </div>
          </div>

          <hr className="border-slate-200" />
          <h4 className="text-sm font-bold text-primary-600 uppercase tracking-wide">EMI Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Principal</label>
              <input type="number" className="w-full border border-slate-300 rounded-lg p-2" value={addLoanData.principal} onChange={(e) => handleAddLoanFieldChange('principal', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Big Interest</label>
              <input type="number" className="w-full border border-slate-300 rounded-lg p-2" value={addLoanData.interest} onChange={(e) => handleAddLoanFieldChange('interest', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Total (Auto)</label>
              <input type="number" className="w-full border border-slate-300 rounded-lg p-2 bg-slate-100" value={addLoanData.total} readOnly />
            </div>
          </div>

          <hr className="border-slate-200" />
          <h4 className="text-sm font-bold text-amber-600 uppercase tracking-wide">Arrear Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Arrear Principal</label>
              <input type="number" className="w-full border border-slate-300 rounded-lg p-2" value={addLoanData.arrearPrincipal} onChange={(e) => handleAddLoanFieldChange('arrearPrincipal', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Arrear Interest</label>
              <input type="number" className="w-full border border-slate-300 rounded-lg p-2" value={addLoanData.arrearInterest} onChange={(e) => handleAddLoanFieldChange('arrearInterest', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Arrear Total (Auto)</label>
              <input type="number" className="w-full border border-slate-300 rounded-lg p-2 bg-slate-100" value={addLoanData.arrearTotal} readOnly />
            </div>
          </div>

          <hr className="border-slate-200" />
          <h4 className="text-sm font-bold text-slate-600 uppercase tracking-wide">Other Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Penalty</label>
              <input type="number" className="w-full border border-slate-300 rounded-lg p-2" value={addLoanData.penalty} onChange={(e) => handleAddLoanFieldChange('penalty', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Penalty Paid</label>
              <input type="number" className="w-full border border-slate-300 rounded-lg p-2" value={addLoanData.penaltyPaid} onChange={(e) => handleAddLoanFieldChange('penaltyPaid', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">EMI Received</label>
              <input type="number" className="w-full border border-slate-300 rounded-lg p-2" value={addLoanData.emiReceived} onChange={(e) => handleAddLoanFieldChange('emiReceived', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Outstanding</label>
              <input type="number" className="w-full border border-slate-300 rounded-lg p-2" value={addLoanData.outstanding} onChange={(e) => handleAddLoanFieldChange('outstanding', e.target.value)} />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
          <Button variant="secondary" onClick={() => setAddLoanModalOpen(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveNewLoanRow} disabled={savingNewLoan}>
            {savingNewLoan ? <Spinner size="sm" /> : 'Create Row'}
          </Button>
        </div>
      </Modal>

      {/* Header and PDF Options */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-4 pb-3 border-b border-slate-200">
        <h3 className="text-lg font-bold text-slate-800">
          {accountType === "01" ? "Marginal Money Statement" :
            accountType === "02" ? "Fixed Deposit Statement" :
              accountType === "04" ? "Loan Statement" :
                accountType === "05" ? "Chit Statement" :
                  "Account Statement"}
        </h3>

        <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto items-stretch sm:items-center">
          {/* Active / Closed Loan Switcher */}
          <div className="flex flex-wrap gap-1">
            {loanData?.loans?.length > 0 ? (
              loanData.loans.map((loan, index) => {
                const isSelected = loanData.loanDate === loan.loanDate &&
                  loanData.totalLoanAmount === loan.totalLoanAmount;
                let status = 'Active';
                let colorClass = 'bg-primary-100 text-primary-800 border-primary-200';
                if (loan.isForeclosed || loan.loanStatus === 'foreclosed') {
                  status = 'Foreclosed';
                  colorClass = 'bg-danger-100 text-danger-800 border-danger-200';
                } else if (loan.isLoanClosed) {
                  status = 'Closed';
                  colorClass = 'bg-success-100 text-success-800 border-success-200';
                }

                return (
                  <button
                    key={index}
                    onClick={() => handleLoanSwitch(index)}
                    className={`px-2 py-1 text-xs font-semibold border rounded-lg transition-all flex flex-col items-center ${isSelected
                      ? 'bg-primary-500 text-white border-primary-700 shadow'
                      : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                  >
                    <span className="text-[10px] uppercase font-bold">{status} - {formatCurrency(loan.totalLoanAmount)}</span>
                    {loan.loanDate && loan.loanDate !== 'N/A' && (
                      <span className="text-[9px] opacity-80 font-normal">({loan.loanDate})</span>
                    )}
                  </button>
                );
              })
            ) : (
              <span className="text-xs text-slate-500 bg-slate-100 px-2.5 py-1.5 rounded-lg border">No Loans Found</span>
            )}
          </div>

          {/* Action Button Group */}
          <div className="flex items-center gap-1.5 justify-end">
            <Button variant="secondary" size="sm" onClick={() => setOpenPdfModal(true)}>
              <FaFilePdf className="mr-1.5" /> PDF Statement
            </Button>

            <Button variant="primary" size="sm" onClick={handleForeclosureClick}>
              <FaLock className="mr-1.5" /> Foreclosure
            </Button>

            {isLoanAdmin && (
              <div className="flex gap-1">
                <Button variant="secondary" size="sm" onClick={handleAddLoanRowOpen}>
                  <FaPlus className="mr-1" /> Row
                </Button>
                <button
                  onClick={handleDeleteAllLoanStatements}
                  className="p-2 border border-danger-200 rounded-lg bg-danger-50 text-danger-600 hover:bg-danger-100 transition-colors"
                  title="Delete All"
                >
                  <FaTrashAlt size={16} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PDF Preview Modal */}
      <Modal
        isOpen={openPdfModal}
        onClose={() => setOpenPdfModal(false)}
        title="PDF Preview"
        size="xl"
      >
        <div className="h-[60vh] w-full bg-slate-100 rounded-lg overflow-hidden">
          <PDFViewer width="100%" height="100%">
            <LoanStatementPDF
              customer={selectedOption}
              loanData={loanData}
              summaryData={summaryData}
              filteredPaymentData={filteredPaymentData}
            />
          </PDFViewer>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <PDFDownloadLink
            document={
              <LoanStatementPDF
                customer={selectedOption}
                loanData={loanData}
                summaryData={summaryData}
                filteredPaymentData={filteredPaymentData}
              />
            }
            fileName={`${selectedOption.name}_${selectedOption.customerUniqueId}_Statement.pdf`}
          >
            {({ loading }) => (
              <Button variant="primary" disabled={loading}>
                {loading ? "Generating..." : "Download PDF"}
              </Button>
            )}
          </PDFDownloadLink>
          <Button variant="secondary" onClick={() => setOpenPdfModal(false)}>Close</Button>
        </div>
      </Modal>

      {/* Summary Cards */}
      {summaryData.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {summaryData.map((item, idx) => {
            const accent = item.type === 'success' ? 'success' :
              item.type === 'danger' ? 'danger' :
                item.type === 'warning' ? 'warning' : 'primary';
            const customBg = item.type === 'success' ? 'bg-[#E5F3E7]' :
              item.type === 'danger' ? 'bg-[#FDF2F2]' :
                item.type === 'warning' ? 'bg-[#FDF4E7]' : 'bg-[#ECEFF7]';
            const glowClass = accent === 'success' ? 'border-glow-green' :
              accent === 'danger' ? 'border-glow-red' :
                accent === 'warning' ? 'border-glow-amber' : 'border-glow-blue';
            return (
              <StatCard
                key={idx}
                title={item.label}
                value={formatCurrency(item.value)}
                accent={accent}
                customBg={customBg}
                className={`animated-glow-border ${glowClass}`}
              />
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-slate-500 mb-4 bg-slate-50 p-3 rounded-lg border border-dashed text-center">No summary data available.</p>
      )}

      {/* Loan Statement Table */}
      {loadingLoanDetails ? (
        <div className="flex justify-center py-12 bg-white rounded-xl border">
          <Spinner size="lg" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border-light bg-surface-card shadow-sm">
          <table className="w-full text-left border-collapse min-w-[1200px]">
            <thead>
              <tr className="bg-canvas-dark text-ink-700 text-xs font-bold border-b border-border-light">
                <th className="p-3 align-middle text-center font-bold bg-canvas-dark" rowSpan={2}>Due Date</th>
                <th className="p-3 align-middle text-center font-bold bg-canvas-dark" rowSpan={2}>Inst #</th>
                <th className="p-3 align-middle text-center font-bold bg-canvas-dark" rowSpan={2}>Payment Date</th>
                <th className="p-3 align-middle text-center font-bold bg-primary/10 text-primary-text border-x border-border-light" colSpan={3}>EMI Details</th>
                <th className="p-3 align-middle text-center font-bold bg-warning/10 text-warning-text border-x border-border-light" colSpan={3}>Arrears</th>
                <th className="p-3 align-middle text-center font-bold bg-canvas-dark" rowSpan={2}>Status</th>
                <th className="p-3 align-middle text-center font-bold bg-purple-500/10 text-purple-700 border-x border-border-light" colSpan={2}>Penalty</th>
                <th className="p-3 align-middle text-center font-bold bg-canvas-dark" rowSpan={2}>Amt Received</th>
                <th className="p-3 align-middle text-center font-bold bg-canvas-dark" rowSpan={2}>Action</th>
                {isLoanAdmin && <th className="p-3 align-middle text-center font-bold bg-danger/10 text-danger-text" rowSpan={2}>Admin</th>}
              </tr>
              <tr className="bg-canvas-dark/50 text-ink-600 text-[10px] font-semibold border-b border-border-light">
                <th className="p-2 text-center bg-primary/5 text-primary-text border-r border-border-light/50">Principal</th>
                <th className="p-2 text-center bg-primary/5 text-primary-text border-r border-border-light/50">Interest</th>
                <th className="p-2 text-center bg-primary/5 text-primary-text border-r border-border-light/50">Total</th>
                <th className="p-2 text-center bg-warning/5 text-warning-text border-r border-border-light/50">Principal</th>
                <th className="p-2 text-center bg-warning/5 text-warning-text border-r border-border-light/50">Interest</th>
                <th className="p-2 text-center bg-warning/5 text-warning-text border-r border-border-light/50">Total</th>
                <th className="p-2 text-center bg-purple-500/5 text-purple-700 border-r border-border-light/50">Amount</th>
                <th className="p-2 text-center bg-purple-500/5 text-purple-700 border-r border-border-light/50">Paid</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-light text-xs text-ink-700 font-medium">
              {filteredPaymentData.length > 0 ? (
                filteredPaymentData.map((item, index) => (
                  <tr
                    key={index}
                    className={`hover:bg-canvas-dark transition-colors ${index % 2 === 0 ? 'bg-surface-card' : 'bg-canvas-dark/20'}`}
                  >
                    <td className="p-2.5 text-center">{item.dueDate}</td>
                    <td className="p-2.5 text-center font-bold">{item.dueNo}</td>
                    <td className="p-2.5 text-center text-ink-500">
                      {item.received === 'Received' || item.received === 'Foreclosed' ? item.paymentDate : 'Not Paid'}
                    </td>

                    {/* EMI Section */}
                    <td className="p-2.5 text-center bg-primary/5 border-r border-border-light/30">{formatCurrency(item.principal)}</td>
                    <td className="p-2.5 text-center bg-primary/5 border-r border-border-light/30">{formatCurrency(item.interest)}</td>
                    <td className="p-2.5 text-center bg-primary/10 border-r border-border-light/40 font-bold text-primary-text">{formatCurrency(item.total)}</td>

                    {/* Arrears Section */}
                    <td className="p-2.5 text-center bg-warning/5 border-r border-border-light/30 text-warning-text">{formatCurrency(item.arrearPrincipal)}</td>
                    <td className="p-2.5 text-center bg-warning/5 border-r border-border-light/30 text-warning-text">{formatCurrency(item.arrearInterest)}</td>
                    <td className="p-2.5 text-center bg-warning/10 border-r border-border-light/40 font-bold text-warning-text">{formatCurrency(item.arrearTotal)}</td>

                    {/* Status */}
                    <td className="p-2.5 text-center">
                      <Badge variant={
                        item.received === 'Received' ? 'success' :
                          item.received === 'Foreclosed' ? 'danger' :
                            'warning'
                      } size="sm">
                        {item.received}
                      </Badge>
                    </td>

                    {/* Penalty Section */}
                    <td className="p-2.5 text-center bg-purple-500/5 border-r border-border-light/30 text-purple-700">{formatCurrency(item.penalty)}</td>
                    <td className="p-2.5 text-center bg-purple-500/5 border-r border-border-light/30 text-purple-700">{formatCurrency(item.paid)}</td>

                    <td className="p-2.5 text-center font-bold text-ink-900">
                      {item.received === 'Received' || item.received === 'Foreclosed' ? formatCurrency(item.emiReceived) : '-'}
                    </td>

                    {/* Action */}
                    <td className="p-2.5 text-center">
                      {item.received === 'Pending' && (
                        <Box>
                          {isDueDateInFuture(item.dueDate) ? (
                            <Tooltip
                              title="Future installments cannot be marked as received until their due date has passed"
                              arrow
                              placement="top"
                            >
                              <span>
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  size="small"
                                  disabled={true}
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    minWidth: '90px',
                                    opacity: 0.7,
                                    bgcolor: '#9e9e9e',
                                  }}
                                >
                                  Future Due
                                </Button>
                              </span>
                            </Tooltip>
                          ) : (
                            (() => {
                              // Computed ONCE for both the button and its warning
                              // label. The affordability test used to live inside
                              // the label's own IIFE, so the button could not see
                              // it: the screen printed "✗ Low Balance" and offered
                              // an enabled Mark Received directly above it.
                              //
                              // Arrears are included because that is what the
                              // server charges — collectStatement bills
                              // total + arrearTotal.
                              const itemDue = Number(item.total || 0) + Number(item.arrearTotal || 0);

                              // KNOWN, not merely falsy. `balance` is assigned
                              // `data?.balance || 0` inside a fetch whose .catch
                              // returns an empty object, so a failed request looks
                              // exactly like a member with no money. Disabling on
                              // `balance === 0` would grey this button out for
                              // EVERY member during an API hiccup and stop all
                              // collection.
                              //
                              // Unknown balance therefore leaves the button live and
                              // lets the server decide; it refuses an unaffordable
                              // instalment safely, writing nothing.
                              const balanceKnown =
                                selectedOption?.balance !== undefined &&
                                selectedOption?.balance !== null;
                              const hasSufficientBalance =
                                Number(selectedOption?.balance || 0) >= itemDue && itemDue > 0;
                              const cannotAfford =
                                balanceKnown && itemDue > 0 && Number(selectedOption.balance) < itemDue;

                              const money = (n) =>
                                `₹${Number(n || 0).toLocaleString('en-IN', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`;

                              const markReceivedButton = (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  disabled={processingPayment || cannotAfford}
                                  onClick={() => handlePayLoanClick(item)}
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    minWidth: '90px',
                                  }}
                                >
                                  Mark Received
                                </Button>
                              );

                              return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                  {/* Tooltip only when the shortfall is the reason.
                                      A button greyed by processingPayment must not
                                      gain an explanation that does not apply.
                                      The <span> is required — MUI tooltips do not
                                      fire on a disabled button without it. Same
                                      pattern as the Future Due button above. */}
                                  {cannotAfford ? (
                                    <Tooltip
                                      title={`Balance ${money(selectedOption.balance)} is less than the ${money(itemDue)} due on this instalment.`}
                                      arrow
                                      placement="top"
                                    >
                                      <span>{markReceivedButton}</span>
                                    </Tooltip>
                                  ) : (
                                    markReceivedButton
                                  )}
                                  {/* Balance indicator - reads the same figures as
                                      the button, so the two cannot disagree. */}
                                  {balanceKnown && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: hasSufficientBalance ? '#2e7d32' : '#d32f2f',
                                        fontWeight: 'bold',
                                        fontSize: '10px',
                                      }}
                                    >
                                      {hasSufficientBalance ? '✓ Balance OK' : '✗ Low Balance'}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })()
                          )}
                        </Box>
                      )}
                      {item.received === 'Received' && (
                        <span className="text-success font-bold flex items-center justify-center gap-1">
                          <FaCheckCircle /> Paid
                        </span>
                      )}
                      {item.received === 'Foreclosed' && (
                        <span className="text-danger font-bold flex items-center justify-center gap-1">
                          <FaLock /> Closed
                        </span>
                      )}
                    </td>

                    {/* Admin */}
                    {isLoanAdmin && (
                      <td className="p-2.5 text-center bg-danger/5">
                        <div className="flex gap-1 justify-center">
                          <Button
                            variant="secondary-ghost"
                            size="sm"
                            iconOnly
                            onClick={() => handleEditLoanStatement(item)}
                            title="Edit row"
                          >
                            <FaEdit className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="danger-ghost"
                            size="sm"
                            iconOnly
                            onClick={() => handleDeleteLoanStatement(item.id)}
                            title="Delete row"
                          >
                            <FaTrash className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isLoanAdmin ? 15 : 14} className="text-center py-10 text-ink-500 bg-canvas-dark/20">
                    <div className="max-w-md mx-auto py-4">
                      <p className="font-semibold text-ink-900">
                        {loanStatus !== activeFilter ? `This is a ${loanStatus?.toUpperCase()} loan.` : 'No Loan Statement Found'}
                      </p>
                      {loanStatus !== activeFilter && (
                        <p className="text-xs text-ink-400 mt-1">Please switch filter to view details.</p>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {(() => {
            const foreclosed = filteredPaymentData.filter(p => p.received === 'Foreclosed');
            if (!foreclosed.length) return null;
            const settled = foreclosed.reduce((s, p) => s + (Number(p.emiReceived) || 0), 0);
            const principal = foreclosed.reduce((s, p) => {
              const own = Number(p.principal) || 0;
              const arr = Number(p.arrearPrincipal) || 0;
              return s + (own > 0 && arr > 0 ? Math.max(own, arr) : own + arr);
            }, 0);
            const charges = Math.round((settled - principal) * 100) / 100;
            return (
              <div className="px-4 py-3 text-xs text-ink-700 border-t border-border-light bg-danger/5">
                <span className="font-bold">Foreclosure settlement {formatCurrency(settled)}</span>
                {foreclosed[0].paymentDate ? ` on ${foreclosed[0].paymentDate}` : ''}
                {' '}(principal {formatCurrency(principal)}
                {charges !== 0 ? ` + other ${formatCurrency(charges)}` : ''}).
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};


/* ==================== MAIN COMPONENT: MemberDetails ==================== */
const MemberDetails = () => {
  const { permissions, roleName, data, jwt } = useAuth();

  // Replaces alert(). The channel-guard refusal is 150+ characters and reads
  // badly in a native dialog; this also stops the tab freezing behind it.
  const { showError, showSuccess, showWarning, SnackbarHost } = useSnackbar();

  const normalizedRole = String(roleName || "").toLowerCase().trim();
  const isSuperAdmin = normalizedRole === "superadmin";
  const isAdmin = normalizedRole === "admin";
  const isDataEntryOperator = roleName === "Data Entry Operator";

  const userBranchId = data?.branchId;
  const shouldFilterByBranch = !isSuperAdmin && userBranchId;

  const [searchType, setSearchType] = useState("name");
  const [searchValue, setSearchValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [accountType, setAccountType] = useState("01");
  const [narration, setNarration] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showLoanStatement, setShowLoanStatement] = useState(false);
  const [transactionType, setTransactionType] = useState("deposit");
  const [cash_type, setCash_type] = useState("");
  const [amount, setAmount] = useState("");
  const [interestRate, setInterestRate] = useState("");
  const [tenure, setTenure] = useState("");
  const [chitValue, setChitValue] = useState("");
  const [cashValues, setCashValues] = useState({
    500: 0,
    200: 0,
    100: 0,
    50: 0,
    20: 0,
    10: 0,
    5: 0,
    change: 0,
  });
  const [totalAmount, setTotalAmount] = useState(0);
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState(today);
  const [openDocumentModal, setOpenDocumentModal] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [excelData, setExcelData] = useState(null);
  const [loanData, setLoanData] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState(today);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openMarginalMoneyModal, setOpenMarginalMoneyModal] = useState(false);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [smsPayload, setSmsPayload] = useState({ phoneNo: "", message: "" });

  // Admin transaction delete state
  const [selectedTxnIds, setSelectedTxnIds] = useState([]);
  const [deletingTxns, setDeletingTxns] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (selectedOption && amount) {
      let closingBalance;
      let message = "";

      if (transactionType === "deposit") {
        closingBalance = (selectedOption.balance || 0) + (parseFloat(amount) || 0);
        message = `Your account ${selectedOption.name} is credited with ${amount} and available balance is ${0}. JLNLTD`;
      } else if (transactionType === "withdraw") {
        closingBalance = (selectedOption.balance || 0) - (parseFloat(amount) || 0);
        message = `Your account ${selectedOption.name} is debited with ${amount} and available balance is ${0}. JLNLTD`;
      }

      if (message) {
        setSmsPayload({
          phoneNo: selectedOption.mobileNumber || "",
          message: message
        });
      } else {
        setSmsPayload({ phoneNo: "", message: "" });
      }
    } else {
      setSmsPayload({ phoneNo: "", message: "" });
    }
  }, [selectedOption, amount, transactionType]);

  useEffect(() => {
    const total = Object.entries(cashValues).reduce((acc, [denomination, count]) => {
      if (denomination === "change") return acc + count;
      return acc + Number(denomination) * count;
    }, 0);
    setTotalAmount(total);
  }, [cashValues]);

  useEffect(() => {
    setShowForm(false);
    setShowTransactions(false);
    setShowLoanStatement(false);
    setSelectedOption(null);
    setSearchValue("");
    setSearchResults([]);
    setCashValues({ 500: 0, 200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, change: 0 });
    setTotalAmount(0);
    setExcelData(null);
    setSelectedImage(null);
    setFromDate("");
    setToDate(today);
    setOpenMarginalMoneyModal(false);
    setShowAllTransactions(false);
    setCash_type("");
    setReferenceNumber("");
  }, [accountType]);

  const fetchSearchResults = async () => {
    if (!searchValue) {
      setSearchResults([]);
      setSelectedOption(null);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        searchInput: searchValue,
        accountType,
        limit: 20
      };

      if (shouldFilterByBranch) {
        payload.branchId = userBranchId;
        console.log(`Fast search with branchId ${userBranchId} for ${roleName}`);
      }

      const response = await apiCall(
        `${API_BASE_URL}/api/v2/search-fast`,
        'post',
        payload
      );

      let filteredResults = response.filter((result) => {
        if (searchType === "name") {
          return result.name?.toLowerCase().includes(searchValue.toLowerCase());
        } else if (searchType === "uniqueId") {
          return result.customerUniqueId?.toLowerCase().includes(searchValue.toLowerCase());
        } else if (searchType === "phone") {
          const cleanInput = searchValue.replace(/\D/g, "");
          const cleanMobile = result.mobileNumber?.toString().replace(/\D/g, "");
          return cleanMobile?.includes(cleanInput);
        }
        return true;
      });

      if (shouldFilterByBranch) {
        filteredResults = filteredResults.filter(result =>
          result.branchId && String(result.branchId) === String(userBranchId)
        );
      }

      setSearchResults(filteredResults);
    } catch (error) {
      console.error("Error fetching search results:", error);
      try {
        console.log("Falling back to legacy search...");
        const legacyPayload = {
          searchInput: searchValue,
          searchType,
          accountType
        };
        if (shouldFilterByBranch) {
          legacyPayload.filter = JSON.stringify({ branchId: userBranchId });
        }
        const legacyResponse = await apiCall(
          `${API_BASE_URL}/api/searchByNamePhoneNoAccNoCusUniqueId`,
          'post',
          legacyPayload
        );
        setSearchResults(legacyResponse || []);
      } catch (fallbackError) {
        console.error("Legacy search also failed:", fallbackError);
        setSearchResults([]);
        showError("Failed to connect to server. Please check your network connection or contact support.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOption = async (option) => {
    setSelectedOption(option);
    setSearchValue("");
    setSearchResults([]);
    setShowTransactions(false);
    setShowForm(false);
    setShowLoanStatement(false);
    setExcelData(null);
    setSelectedImage(null);
    setFromDate("");
    setToDate(today);
    setShowAllTransactions(false);

    if (option.customerUniqueId && !option.fk_personal_details_hasMany_transactions_customerUniqueId) {
      try {
        setLoading(true);

        const [transactionsResponse, accountsResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/v2/customer-transactions?customerUniqueId=${option.customerUniqueId}`)
            .then(res => res.json())
            .catch(() => ({ data: { transactions: [] } })),
          fetch(`${API_BASE_URL}/api/v2/customer-accounts?customerUniqueId=${option.customerUniqueId}`)
            .then(res => res.json())
            .catch(() => ({ data: { accounts: [] } }))
        ]);

        const enrichedOption = {
          ...option,
          fk_personal_details_hasMany_transactions_customerUniqueId: transactionsResponse.data?.transactions || [],
          fk_personal_details_hasMany_account_details_customerUniqueId: accountsResponse.data?.accounts || [],
          balance: transactionsResponse.data?.balance || 0,
          accountNumbers: (accountsResponse.data?.accounts || []).map(a => a.accountNumber)
        };

        setSelectedOption(enrichedOption);
      } catch (error) {
        console.error("Error fetching customer details:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const calculateClosingBalance = (transactions) => {
    if (!transactions || transactions.length === 0) {
      return { transactions: [], totalDeposits: 0, totalWithdrawals: 0, closingBalance: 0 };
    }

    let balance = 0;
    let totalDeposits = 0;
    let totalWithdrawals = 0;

    const sortedByDateAsc = [...transactions].sort((a, b) => {
      const dateA = a.date ? new Date(a.date) : new Date(0);
      const dateB = b.date ? new Date(b.date) : new Date(0);
      const diffDate = dateA - dateB;
      if (diffDate !== 0) return diffDate;

      const createdA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const createdB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      const diffCreated = createdA - createdB;
      if (diffCreated !== 0) return diffCreated;

      return (a.id || 0) - (b.id || 0);
    });

    const formattedTransactions = sortedByDateAsc.map((transaction) => {
      const credit = transaction.transactionType === "deposit" ? parseFloat(transaction.amount) || 0 : 0;
      const debit = transaction.transactionType === "withdraw" ? parseFloat(transaction.amount) || 0 : 0;
      balance += credit - debit;
      totalDeposits += credit;
      totalWithdrawals += debit;
      return { ...transaction, credit, debit, closingBalance: balance };
    });

    return {
      transactions: formattedTransactions,
      totalDeposits,
      totalWithdrawals,
      closingBalance: balance,
    };
  };

  const fetchUpdatedTransactions = async () => {
    if (!selectedOption) return;
    try {
      const transactionsResponse = await fetch(
        `${API_BASE_URL}/api/v2/customer-transactions?customerUniqueId=${selectedOption.customerUniqueId}`
      ).then(res => res.json());

      if (transactionsResponse.status === 'success' && transactionsResponse.data) {
        setSelectedOption(prev => ({
          ...prev,
          fk_personal_details_hasMany_transactions_customerUniqueId: transactionsResponse.data.transactions || [],
          balance: transactionsResponse.data.balance || 0
        }));
      }
    } catch (error) {
      console.error("Error fetching updated transactions:", error);
      try {
        const payload = {
          searchInput: selectedOption.customerUniqueId,
          searchType: "uniqueId",
          accountType
        };
        if (shouldFilterByBranch) {
          payload.filter = JSON.stringify({ branchId: userBranchId });
        }
        const response = await apiCall(
          `${API_BASE_URL}/api/searchByNamePhoneNoAccNoCusUniqueId`,
          'post',
          payload
        );
        const updatedOption = response.find(
          (result) => result.customerUniqueId === selectedOption.customerUniqueId
        );
        if (updatedOption) {
          setSelectedOption(updatedOption);
        }
      } catch (fallbackError) {
        console.error("Fallback also failed:", fallbackError);
      }
    }
  };

  const fetchTransactionDetails = async () => {
    await fetchUpdatedTransactions();
  };

  const resetPaymentModal = () => {
    setAmount("");
    setNarration("");
    setInterestRate("");
    setTenure("");
    setChitValue("");
    setCashValues({ 500: 0, 200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, change: 0 });
    setTotalAmount(0);
    setShowForm(false);
    setOpenConfirmModal(false);
    setOpenMarginalMoneyModal(false);
    setCash_type("");
    setReferenceNumber("");
  };

  const handleInputChange = (denomination, value) => {
    const newValue = value === "" ? 0 : parseInt(value) || 0;
    setCashValues((prevValues) => ({
      ...prevValues,
      [denomination]: newValue,
    }));
  };

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      showWarning("Please enter a valid amount greater than 0.");
      return;
    }
    if (accountType === "01" && cash_type === "cash" && totalAmount !== parseFloat(amount)) {
      setOpenConfirmModal(true);
      return;
    }
    await submitTransaction();
  };

  const submitTransaction = async () => {
    const branchId = data?.branchId || null;

    const payload = {
      model: "transactions",
      type: "Add",
      customerUniqueId: selectedOption.customerUniqueId,
      accountNumber: Array.isArray(selectedOption.accountNumber)
        ? selectedOption.accountNumber[0]
        : selectedOption.accountNumber || "N/A",
      transactionType,
      accountType,
      narration,
      amount: parseFloat(amount),
      date,
      interestRate: accountType === "02" || accountType === "04" ? parseFloat(interestRate) : null,
      tenure: accountType === "02" || accountType === "04" ? parseInt(tenure) : null,
      chitValue: accountType === "05" ? parseFloat(chitValue) : null,
      managerId: data.id.toString(),
      branchId: branchId,
      cash_type: accountType === "01" ? cash_type : null,
      referenceNumber: cash_type === "online" ? referenceNumber : null,
      ...(accountType === "01" && cash_type === "cash" && {
        500: cashValues[500],
        200: cashValues[200],
        100: cashValues[100],
        50: cashValues[50],
        20: cashValues[20],
        10: cashValues[10],
        5: cashValues[5],
        change: cashValues.change,
      }),
    };
    setSubmitting(true);
    try {
      const response = await apiCall(`${API_BASE_URL}/api/addDetails`, 'post', payload);

      let updatedSmsPayload = { ...smsPayload };
      const existingTransactions = filterTransactions(selectedOption.fk_personal_details_hasMany_transactions_customerUniqueId || []);
      const newTransaction = {
        transactionType,
        amount: parseFloat(amount),
        date: new Date()
      };

      const allTransactions = [...existingTransactions, newTransaction];
      const { closingBalance } = calculateClosingBalance(allTransactions);
      const formattedAmount = parseFloat(amount).toFixed(2);

      if (transactionType === "deposit") {
        updatedSmsPayload.message = `Your account ${selectedOption.name} is credited with ${formattedAmount} and available balance is ${closingBalance.toFixed(2)}. JLNLTD`;
      } else if (transactionType === "withdraw") {
        updatedSmsPayload.message = `Your account ${selectedOption.name} is debited with ${formattedAmount} and available balance is ${closingBalance.toFixed(2)}. JLNLTD`;
      }

      let smsSuccess = false;
      let smsSkipped = false;
      if (updatedSmsPayload.phoneNo && updatedSmsPayload.message) {
        try {
          let apiEndpoint = `${API_BASE_URL}/api/send-sms`;
          if (transactionType === "withdraw") {
            apiEndpoint = `${API_BASE_URL}/api/debitsms`;
          }

          const smsRes = await apiCall(
            apiEndpoint,
            'post',
            updatedSmsPayload,
            {
              headers: { "Content-Type": "application/json" },
              timeout: 15000,
            }
          );

          // Read the response, not just the absence of an exception. Setting
          // smsSuccess on any non-throwing call reported "SMS Sent Successfully"
          // for months while deposits delivered nothing — the endpoint returned
          // 200 with blank credentials.
          const body = smsRes?.data ?? smsRes ?? {};
          smsSkipped = body.skipped === true;
          smsSuccess = body.success === true;
        } catch (smsError) {
          console.error("SMS sending failed:", smsError);
        }
      }

      if (smsSuccess) {
        showSuccess("Payment Processed and SMS Sent Successfully");
      } else if (smsSkipped) {
        // SMS is switched off; nothing failed, so say nothing about it.
        showSuccess("Payment Processed Successfully");
      } else {
        showSuccess(
          "Payment Processed Successfully" +
          (smsPayload.phoneNo ? " (SMS Failed to Send)" : "")
        );
      }

      resetPaymentModal();
      fetchTransactionDetails();
    } catch (error) {
      console.error("Payment Processing Error:", error);
      const backendError = error.response?.data?.error;
      if (backendError) {
        showError(backendError);
      } else {
        showError(`Failed to add transaction. Please try again or check your network connection.`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedImage) {
      showWarning("Please select an image to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("model", "personal_details");
    formData.append("type", "Edit");
    formData.append("id", selectedOption.id);
    formData.append("customerProfileImage", selectedImage);

    setUploadingImage(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/addDetailsWithImages`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000,
        retry: 2,
        retryDelay: 1000
      });

      if (response.status === 201 || response.status === 200) {
        showSuccess("Profile image uploaded successfully!");
        try {
          const updatedResponse = await apiCall(
            `${API_BASE_URL}/api/searchByNamePhoneNoAccNoCusUniqueId`,
            'post',
            {
              searchInput: selectedOption.customerUniqueId,
              searchType: "uniqueId",
              accountType,
            }
          );
          const updatedOption = updatedResponse.find(
            (result) => result.customerUniqueId === selectedOption.customerUniqueId
          );
          if (updatedOption) {
            setSelectedOption(updatedOption);
          }
        } catch (fetchError) {
          console.error("Error fetching updated profile:", fetchError);
        }
        setSelectedImage(null);
      } else {
        throw new Error("Failed to upload image");
      }
    } catch (error) {
      console.error("Error uploading image:", error.response?.data || error.message);
      showError(`Failed to upload image. Please try again or check your network connection.`);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCloseDocument = () => setOpenDocumentModal(false);

  const handleOpenDocument = () => {
    if (!selectedOption) return;

    const allTransactions = selectedOption.fk_personal_details_hasMany_transactions_customerUniqueId || [];
    const {
      transactions: allFormattedTransactions,
      totalDeposits: overallTotalDeposits,
      totalWithdrawals: overallTotalWithdrawals,
      closingBalance: overallClosingBalance
    } = calculateClosingBalance(allTransactions);

    const filteredTransactionsForPDF = filterTransactions(allFormattedTransactions);

    navigate('/transaction-pdf', {
      state: {
        customerData: selectedOption,
        transactions: filteredTransactionsForPDF,
        fromDate,
        toDate,
        summary: {
          totalDeposits: overallTotalDeposits,
          totalWithdrawals: overallTotalWithdrawals,
          closingBalance: overallClosingBalance
        }
      }
    });
  };

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = `${selectedOption.name}_${selectedOption.customerUniqueId}_Transaction_Statement`;
    window.print();
    document.title = originalTitle;
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (
      file &&
      (file.type === "application/vnd.ms-excel" ||
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
    ) {
      try {
        const workbook = new ExcelJS.Workbook();
        const arrayBuffer = await file.arrayBuffer();
        await workbook.xlsx.load(arrayBuffer);
        const worksheet = workbook.worksheets[0];

        const rows = [];
        let headers = [];
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) {
            headers = row.values.slice(1);
          } else {
            const rowData = {};
            row.eachCell((cell, colNumber) => {
              const header = headers[colNumber - 1];
              rowData[header] = cell.value;
            });
            rows.push(rowData);
          }
        });

        if (rows.length === 0) {
          showWarning("Excel file is empty.");
          setExcelData(null);
          event.target.value = null;
          return;
        }

        setExcelData(rows);
      } catch (error) {
        console.error("Error parsing Excel file:", error);
        showError("Failed to parse Excel file. Please ensure it's a valid Excel file.");
        setExcelData(null);
        event.target.value = null;
      }
    } else {
      showWarning("Please select a valid Excel file (.xls or .xlsx)");
      setExcelData(null);
      event.target.value = null;
    }
  };

  const handleExcelUpload = async () => {
    if (!selectedOption) {
      showWarning("Please select a customer first.");
      return;
    }
    if (!excelData) {
      showWarning("Please select an Excel file and ensure it contains data.");
      return;
    }

    setUploadingExcel(true);

    const payload = {
      customerUniqueId: selectedOption.customerUniqueId,
      accountType,
      transactions: excelData.map((row) => ({
        date: row["Date"],
        narration: row["Description"],
        openingBalance: row["Opening Balance"],
        credit: row["Credit"],
        debit: row["Debit"],
        closingBalance: row["Closing Balance"],
      })),
    };

    try {
      const response = await apiCall(
        `${API_BASE_URL}/api/uploadExcelTransactionChunk/${selectedOption.customerUniqueId}`,
        'post',
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response.success) {
        showSuccess("Transactions uploaded successfully!");
        setExcelData(null);
        document.getElementById("excel-upload-input").value = null;
        await fetchUpdatedTransactions();
        setShowTransactions(true);
      } else {
        throw new Error(response.errorMsg || "Failed to upload transactions");
      }
    } catch (error) {
      console.error("Error uploading transactions:", error.response?.data || error.message);
      showError(`Failed to upload transactions. Please try again or check your network connection.`);
    } finally {
      setUploadingExcel(false);
    }
  };

  const filterTransactions = (transactions) => {
    if (!fromDate && !toDate) return transactions;
    return transactions.filter((transaction) => {
      if (!transaction.date) return false;
      const transactionDate = new Date(transaction.date);
      if (isNaN(transactionDate.getTime())) return false;

      const from = fromDate ? new Date(fromDate) : null;
      const to = toDate ? new Date(toDate) : null;

      if (to) {
        to.setHours(23, 59, 59, 999);
      }

      return (!from || transactionDate >= from) && (!to || transactionDate <= to);
    });
  };

  const renderDocumentContent = () => {
    if (!selectedOption || !selectedOption.fk_personal_details_hasMany_transactions_customerUniqueId) {
      return <div className="text-center py-6 text-slate-500">No transactions available.</div>;
    }

    const allTransactions = selectedOption.fk_personal_details_hasMany_transactions_customerUniqueId || [];
    const {
      transactions: allFormattedTransactions,
      totalDeposits: overallTotalDeposits,
      totalWithdrawals: overallTotalWithdrawals,
      closingBalance: overallClosingBalance
    } = calculateClosingBalance(allTransactions);

    const transactions = filterTransactions(allFormattedTransactions);

    const {
      totalDeposits,
      totalWithdrawals
    } = calculateClosingBalance(transactions);

    return (
      <div className="p-6 bg-white w-full box-border">
        <style>
          {`
            @media print {
              @page {
                size: A4 portrait;
                margin: 10mm;
              }
              body * {
                visibility: hidden;
              }
              #print-content, #print-content * {
                visibility: visible;
              }
              #print-content {
                position: absolute;
                left: 0;
                top: 0;
                width: 190mm;
                padding: 0;
                box-sizing: border-box;
                font-size: 9px;
              }
              .no-print {
                display: none !important;
              }
              table {
                width: 100%;
                font-family: 'Poppins', sans-serif;
                font-size: 8px;
                border-collapse: collapse;
                table-layout: fixed;
                border: 1.5px solid #000;
                letter-spacing: 0.3px;
              }
              th, td {
                padding: 3px 4px !important;
                border: 1px solid #000 !important;
                word-wrap: break-word;
                word-break: break-all;
                overflow: hidden;
                font-family: 'Poppins', sans-serif;
                font-size: 8px !important;
                line-height: 1.2;
              }
              th {
                background-color: #f5f5f5 !important;
                font-weight: bold !important;
                font-size: 8.5px !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .col-serial { width: 6%; }
              .col-date { width: 12%; }
              .col-txn-id { width: 24%; }
              .col-desc { width: 20%; }
              .col-credit { width: 13%; }
              .col-debit { width: 13%; }
              .col-balance { width: 12%; }
              #print-content h6, #print-content p, #print-content span {
                font-size: 9px !important;
              }
            }
          `}
        </style>

        <div id="print-content" className="space-y-4">
          <div className="text-center">
            <img src={Logo} alt="Jothilinga Logo" className="mx-auto w-32 h-auto mb-2" />
            <h2 className="text-lg font-bold text-slate-800 tracking-wide font-sans">JOTHILINGA FINANCIAL SOLUTIONS PVT.LTD</h2>
            <h3 className="text-md font-semibold text-slate-700 font-sans">Transaction Statement</h3>
            <p className="text-sm text-slate-600 mt-1">{selectedOption.name} ({selectedOption.customerUniqueId})</p>
            <p className="text-xs text-slate-500">Mobile: {selectedOption.mobileNumber} | Address: {selectedOption.permanentStreetAddress || "N/A"}</p>
            <p className="text-xs text-slate-500 mt-0.5">Statement Date: {formatDate(date)}</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-slate-300">
              <thead>
                <tr className="bg-slate-100">
                  <th className="col-serial text-center border p-2 font-bold">No</th>
                  <th className="col-date text-center border p-2 font-bold">Date</th>
                  <th className="col-txn-id text-center border p-2 font-bold">Txn ID</th>
                  <th className="col-desc text-left border p-2 font-bold">Description</th>
                  <th className="col-credit text-center border p-2 font-bold">Credit</th>
                  <th className="col-debit text-center border p-2 font-bold">Debit</th>
                  <th className="col-balance text-center border p-2 font-bold">Balance</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction, index) => (
                  <tr key={index} className="border-b">
                    <td className="col-serial text-center border p-1.5">{index + 1}</td>
                    <td className="col-date text-center border p-1.5">{formatDate(transaction.date)}</td>
                    <td className="col-txn-id text-center border p-1.5 text-slate-600 font-mono">{transaction.transactionId || "N/A"}</td>
                    <td className="col-desc border p-1.5">{transaction.narration || "N/A"}</td>
                    <td className="col-credit text-center border p-1.5 text-success-700 font-semibold">{transaction.credit ? `₹${transaction.credit.toFixed(2)}` : "-"}</td>
                    <td className="col-debit text-center border p-1.5 text-danger-700 font-semibold">{transaction.debit ? `₹${transaction.debit.toFixed(2)}` : "-"}</td>
                    <td className="col-balance text-center border p-1.5 font-bold text-primary-700">₹{transaction.closingBalance.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pt-3 border-t border-slate-200 text-xs space-y-1 bg-slate-50 p-3 rounded-lg">
            <p><strong>Total Deposits:</strong> <span className="text-success-700 font-semibold">{formatCurrency(totalDeposits)}</span></p>
            <p><strong>Total Withdrawals:</strong> <span className="text-danger-700 font-semibold">{formatCurrency(totalWithdrawals)}</span></p>
            <p className="text-sm font-bold"><strong>Closing Balance:</strong> <span className="text-primary-700">{formatCurrency(overallClosingBalance)}</span></p>
          </div>

          <div className="pt-12 flex justify-between text-xs font-semibold text-slate-500">
            <span className="border-t border-slate-400 pt-2 px-6">Customer Signature</span>
            <span className="border-t border-slate-400 pt-2 px-6">Authorized Signature</span>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6 no-print">
          <Button variant="primary" onClick={handlePrint}>
            <FaPrint className="mr-1.5" /> Print Document
          </Button>
        </div>
      </div>
    );
  };

  const handleToggleTxnSelect = (id) => {
    setSelectedTxnIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSelectAllTxns = (transactions, checked) => {
    if (checked) {
      setSelectedTxnIds(transactions.map((t) => t.id));
    } else {
      setSelectedTxnIds([]);
    }
  };

  const handleDeleteSelectedTxns = () => {
    if (selectedTxnIds.length === 0) {
      Swal.fire("Warning", "Please select at least one transaction to delete.", "warning");
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${selectedTxnIds.length} selected transaction(s)?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete selected!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        setDeletingTxns(true);
        try {
          const response = await axios.post(
            `${API_BASE_URL}/api/deleteTransactionsWithBackup`,
            { ids: selectedTxnIds, customerUniqueId: selectedOption?.customerUniqueId },
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + jwt,
              },
            }
          );
          Swal.fire("Deleted!", `${response.data.deletedCount} transaction(s) deleted. Backup saved.`, "success");
          setSelectedTxnIds([]);
          await fetchUpdatedTransactions();
        } catch (error) {
          console.error('Error deleting transactions:', error);
          Swal.fire("Error!", `Failed to delete transactions: ${error.response?.data?.error || error.message}`, "error");
        } finally {
          setDeletingTxns(false);
        }
      }
    });
  };

  const handleDeleteAllTxns = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Are you sure you want to delete ALL transactions for this customer? You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete all!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        setDeletingTxns(true);
        try {
          const response = await axios.post(
            `${API_BASE_URL}/api/deleteTransactionsWithBackup`,
            { deleteAll: true, customerUniqueId: selectedOption?.customerUniqueId },
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + jwt,
              },
            }
          );
          Swal.fire("Deleted!", `${response.data.deletedCount} transaction(s) deleted. Backup saved.`, "success");
          setSelectedTxnIds([]);
          await fetchUpdatedTransactions();
        } catch (error) {
          console.error('Error deleting transactions:', error);
          Swal.fire("Error!", `Failed to delete transactions: ${error.response?.data?.error || error.message}`, "error");
        } finally {
          setDeletingTxns(false);
        }
      }
    });
  };

  const handleDeleteSingleTxn = (transactionId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Are you sure you want to delete this transaction?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        setDeletingTxns(true);
        try {
          const response = await axios.post(
            `${API_BASE_URL}/api/deleteTransactionsWithBackup`,
            { ids: [transactionId], customerUniqueId: selectedOption?.customerUniqueId },
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + jwt,
              },
            }
          );
          Swal.fire("Deleted!", `${response.data.deletedCount} transaction(s) deleted. Backup saved.`, "success");
          setSelectedTxnIds(prev => prev.filter(id => id !== transactionId));
          await fetchUpdatedTransactions();
        } catch (error) {
          console.error('Error deleting transaction:', error);
          Swal.fire("Error!", `Failed to delete transaction: ${error.response?.data?.error || error.message}`, "error");
        } finally {
          setDeletingTxns(false);
        }
      }
    });
  };

  const renderMarginalMoneyForm = () => {
    const isAmountValid = amount && parseFloat(amount) > 0;
    const isDenominationValid = cash_type === "cash" ? totalAmount === parseFloat(amount) : true;

    return (
      <div className="bg-white p-6 rounded-xl max-w-2xl w-full mx-auto shadow-lg space-y-4">
        <h3 className="text-lg font-bold text-slate-800 text-center mb-4">Add Marginal Money Transaction</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Transaction Type</label>
            <select className="w-full border border-slate-300 rounded-lg p-2.5 bg-white text-sm" value={transactionType} onChange={(e) => setTransactionType(e.target.value)}>
              <option value="deposit">Deposit</option>
              <option value="withdraw">Withdraw</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Amount</label>
            <input type="number" min="0" step="0.01" className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Enter Amount" />
            {!isAmountValid && amount !== "" && (
              <span className="text-[11px] text-danger-600 block mt-1">Please enter a valid amount greater than 0</span>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Narration</label>
            <input type="text" className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" value={narration} onChange={(e) => setNarration(e.target.value)} placeholder="Enter narration" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Select Date</label>
            <input type="date" min={today} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Cash Type</label>
            <select className="w-full border border-slate-300 rounded-lg p-2.5 bg-white text-sm" value={cash_type} onChange={(e) => setCash_type(e.target.value)}>
              <option value="">Select Cash Type</option>
              <option value="online">Online</option>
              <option value="cash">Cash</option>
            </select>
          </div>

          {cash_type === "cash" && (
            <div className="md:col-span-2 border-t pt-3 mt-2">
              <h4 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wider">Denominations</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[500, 200, 100, 50, 20, 10, 5].map((denomination) => (
                  <div key={denomination}>
                    <label className="block text-xs text-slate-500 mb-1">{denomination} x</label>
                    <input type="number" min="0" step="1" className="w-full border border-slate-300 rounded-lg p-1.5 text-xs" value={cashValues[denomination] || ""} onChange={(e) => handleInputChange(denomination, e.target.value)} placeholder="0" />
                  </div>
                ))}
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Change (₹)</label>
                  <input type="number" min="0" className="w-full border border-slate-300 rounded-lg p-1.5 text-xs" value={cashValues.change || ""} onChange={(e) => handleInputChange("change", e.target.value)} placeholder="0" />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Total Denomination Amount</label>
                <input type="number" className="w-full border border-slate-300 rounded-lg p-2 bg-slate-100 font-bold text-primary-700" value={totalAmount} readOnly />
              </div>
              {isAmountValid && !isDenominationValid && (
                <p className="text-xs text-danger-600 font-medium mt-2">
                  ⚠️ The entered amount (₹{parseFloat(amount).toFixed(2)}) does not match the total denomination amount (₹{totalAmount.toFixed(2)}). Please correct the denominations.
                </p>
              )}
            </div>
          )}

          {cash_type === "online" && (
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Reference Number</label>
              <input type="text" className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} placeholder="Enter Reference Number" />
            </div>
          )}
        </div>

        <div className="flex justify-between items-center pt-4 border-t mt-4">
          <Button variant="secondary" onClick={() => setOpenMarginalMoneyModal(false)}>Cancel</Button>
          {isAmountValid && (cash_type === "online" || (cash_type === "cash" && isDenominationValid)) && (
            <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Spinner size="sm" /> : "Submit"}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <PageLayout contentClassName="overflow-y-auto pb-8">
      {/* Renders through a portal to document.body, so its position here does
          not matter — but it must be rendered somewhere or nothing appears. */}
      <SnackbarHost />
      {/* <PageHeader
        title="Member Details"
        subtitle="Search and manage account statements, transaction history, and loan details"
      /> */}

      <div className="space-y-6">
        {/* Search Criteria Card */}
        <Card>
          <Card.Body>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 items-end">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Account Type</label>
                <div className="relative w-full">
                  <select
                    value={accountType}
                    onChange={(e) => setAccountType(e.target.value)}
                    className="w-full appearance-none bg-white border border-slate-200 rounded-lg pl-3 pr-8 h-[38px] text-xs font-semibold text-slate-700 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-all shadow-sm"
                  >
                    <option value="01">Marginal Money</option>
                    <option value="02">Fixed Deposit</option>
                    <option value="04">Loan</option>
                    <option value="05">Chit</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-500">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Search By</label>
                <div className="relative w-full">
                  <select
                    value={searchType}
                    onChange={(e) => {
                      setSearchType(e.target.value);
                      setSearchValue("");
                    }}
                    className="w-full appearance-none bg-white border border-slate-200 rounded-lg pl-3 pr-8 h-[38px] text-xs font-semibold text-slate-700 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-all shadow-sm"
                  >
                    <option value="name">Name</option>
                    <option value="uniqueId">Unique ID</option>
                    <option value="phone">Phone Number</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-500">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="sm:col-span-2 flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Search Value</label>
                  <SearchInput
                    placeholder={`Enter customer ${searchType === "uniqueId" ? "ID" : searchType === "phone" ? "phone number" : "name"}`}
                    value={searchValue}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (searchType === "name" && /^[a-zA-Z\s]*$/.test(val)) {
                        setSearchValue(val);
                      } else if (searchType === "uniqueId" && /^[a-zA-Z0-9-]*$/.test(val)) {
                        setSearchValue(val);
                      } else if (searchType === "phone" && /^[0-9]*$/.test(val)) {
                        setSearchValue(val);
                      } else if (searchType !== "name" && searchType !== "uniqueId" && searchType !== "phone") {
                        setSearchValue(val);
                      }
                    }}
                    className="w-full sm:!w-full [&>input]:h-[38px] [&>input]:rounded-lg"
                  />
                </div>
                <Button
                  variant="primary"
                  onClick={fetchSearchResults}
                  disabled={loading}
                  className="h-[38px] px-6 text-xs font-bold"
                >
                  {loading ? <Spinner size="sm" /> : "Search"}
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <Card>
            <Card.Body>
              <h3 className="text-sm font-semibold text-[#1E293B] mb-4 font-display">Search Results</h3>
              <Table>
                <Table.Head>
                  <tr>
                    <Table.HeadCell>Name</Table.HeadCell>
                    <Table.HeadCell>Mobile Number</Table.HeadCell>
                    <Table.HeadCell>Unique ID</Table.HeadCell>
                  </tr>
                </Table.Head>
                <Table.Body>
                  {searchResults.map((result, index) => (
                    <Table.Row
                      key={index}
                      onClick={() => handleSelectOption(result)}
                    >
                      <Table.Cell className="font-semibold text-primary">{result.name}</Table.Cell>
                      <Table.Cell>{result.mobileNumber}</Table.Cell>
                      <Table.Cell className="font-mono">{result.customerUniqueId}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Card.Body>
          </Card>
        )}

        {/* Selected Member Details */}
        {selectedOption && (
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-surface-card via-surface-card to-primary/[0.02] border-border-light shadow-card hover:shadow-card-hover transition-all duration-300">
              <Card.Body>
                <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
                  {/* Left: Avatar & Details */}
                  <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 flex-1 w-full">
                    {/* Avatar Container with Hover Upload */}
                    <div className="relative shrink-0 ring-4 ring-primary/5 rounded-full">
                      <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full border-2 border-border-medium overflow-hidden group bg-canvas-dark flex items-center justify-center shadow-inner">
                        {selectedOption.customerProfileImage ? (
                          <img
                            src={`${API_BASE_URL}/images/${selectedOption.customerProfileImage}`}
                            alt="Customer Profile"
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : (
                          <FaUser className="w-10 h-10 text-ink-400" />
                        )}

                        {/* Hover Overlay to Select Image */}
                        <label
                          htmlFor="profile-image-upload"
                          className="absolute inset-0 bg-ink-900/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer"
                        >
                          <FaUpload className="text-white w-5 h-5" />
                        </label>
                      </div>

                      {/* Active Status Indicator Dot */}
                      <span className="absolute bottom-1 right-1 w-4 h-4 bg-success border-2 border-surface-card rounded-full shadow-[0_0_8px_rgba(34,197,94,0.5)]" />

                      {/* Hidden File Input */}
                      <input
                        type="file"
                        id="profile-image-upload"
                        accept="image/*"
                        onChange={(e) => setSelectedImage(e.target.files[0])}
                        className="hidden"
                      />
                    </div>

                    {/* Customer Info Details */}
                    <div className="space-y-3 flex-1 text-center sm:text-left">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 justify-center sm:justify-start">
                        <h3 className="text-2xl font-bold text-ink-900 font-display tracking-tight">
                          {selectedOption.name}
                        </h3>
                        <div className="flex justify-center">
                          <Badge variant="success" size="sm" dot>
                            Active
                          </Badge>
                        </div>
                      </div>

                      {/* Meta Details: ID & Phone */}
                      <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-4 gap-y-1.5 text-sm text-ink-500 font-medium">
                        <div className="flex items-center gap-1.5">
                          <FaUser className="text-primary text-xs" />
                          <span className="font-mono">{selectedOption.customerUniqueId}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <FaPhone className="text-primary text-xs" />
                          <span>{selectedOption.mobileNumber}</span>
                        </div>
                      </div>

                      {/* Address */}
                      <div className="flex items-start justify-center sm:justify-start gap-1.5 text-sm text-ink-500 font-medium">
                        <FaMapMarkerAlt className="text-primary text-xs shrink-0 mt-0.5" />
                        <span className="max-w-md">{selectedOption.permanentStreetAddress || "N/A"}</span>
                      </div>

                      {/* Image Upload Status & Actions */}
                      {selectedImage && (
                        <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 pt-1">
                          <span className="text-xs text-ink-500 font-semibold bg-canvas-dark border border-border-light px-2.5 py-1.5 rounded-md shadow-sm">
                            Selected: {selectedImage.name}
                          </span>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleImageUpload}
                            disabled={uploadingImage}
                          >
                            {uploadingImage ? <Spinner size="sm" /> : "Upload Image"}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedImage(null)}
                            disabled={uploadingImage}
                            className="!text-danger hover:!bg-danger/10"
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex flex-wrap items-center justify-center gap-2.5 shrink-0 w-full lg:w-auto pt-4 lg:pt-0 border-t lg:border-t-0 border-border-light">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setShowLoanStatement(!showLoanStatement);
                        setShowTransactions(false);
                      }}
                      leftIcon={<FaFileInvoiceDollar />}
                    >
                      {showLoanStatement ? "Hide Statement" : "View Statement"}
                    </Button>

                    <Button
                      variant="secondary"
                      onClick={() => {
                        setShowTransactions(!showTransactions);
                        setShowLoanStatement(false);
                      }}
                      leftIcon={<FaCoins />}
                    >
                      {showTransactions ? "Hide Transactions" : "View Transactions"}
                    </Button>

                    <Button
                      variant={showForm && accountType !== "01" ? "danger" : "primary"}
                      onClick={() => {
                        if (accountType === "01") {
                          setOpenMarginalMoneyModal(true);
                        } else {
                          setShowForm(!showForm);
                        }
                      }}
                      leftIcon={<FaPlus />}
                    >
                      {showForm && accountType !== "01"
                        ? "Cancel"
                        : `Add ${accountType === "01" ? "Marginal Money" :
                          accountType === "02" ? "Fixed Deposit" :
                            accountType === "04" ? "Loan" : "Chit"}`}
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>



            {/* Loan / Chit / FD Statement View */}
            {showLoanStatement && (
              <Card>
                <Card.Body>
                  <LoanStatement
                    selectedOption={selectedOption}
                    showLoanStatement={showLoanStatement}
                    setLoanData={setLoanData}
                    accountType={accountType}
                  />
                </Card.Body>
              </Card>
            )}

            {/* Transaction History Section */}
            {showTransactions && (
              <Card>
                <Card.Body>
                  <div className="space-y-6">
                    {/* Clean heading styled like Marginal Money Statement */}
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 pb-3 border-b border-slate-200">
                      <h3 className="text-lg font-bold text-slate-800">Transaction History</h3>
                    </div>
                    {/* Filters & Document Upload Bar */}
                    <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-4 bg-canvas-dark p-3 rounded-xl border border-border-light">
                      <div className="flex flex-col sm:flex-row gap-4 items-center flex-1">
                        <div className="w-full sm:w-auto flex items-center gap-1.5 text-xs font-bold text-ink-500 uppercase">
                          <span>From:</span>
                          <DatePicker
                            selected={fromDate}
                            onChange={(dateStr) => setFromDate(dateStr)}
                            maxDate={toDate ? new Date(toDate) : (today ? new Date(today) : new Date())}
                            className="border border-border-light bg-surface-card text-ink-700 rounded-lg text-xs font-semibold h-[34px] px-2.5 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all shadow-sm w-32"
                            placeholder="DD-MM-YYYY"
                          />
                        </div>
                        <div className="w-full sm:w-auto flex items-center gap-1.5 text-xs font-bold text-ink-500 uppercase">
                          <span>To:</span>
                          <DatePicker
                            selected={toDate}
                            onChange={(dateStr) => setToDate(dateStr)}
                            minDate={fromDate ? new Date(fromDate) : null}
                            maxDate={today ? new Date(today) : new Date()}
                            className="border border-border-light bg-surface-card text-ink-700 rounded-lg text-xs font-semibold h-[34px] px-2.5 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all shadow-sm w-32"
                            placeholder="DD-MM-YYYY"
                          />
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 items-center justify-end">
                        <Button variant="secondary" size="sm" onClick={handleOpenDocument}>
                          <FaFileAlt className="mr-1.5" /> View Document
                        </Button>

                        <input
                          type="file"
                          accept=".xls,.xlsx"
                          onChange={handleFileChange}
                          className="hidden"
                          id="excel-upload-input"
                        />
                        <label htmlFor="excel-upload-input" className="cursor-pointer">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border-light text-ink-700 bg-surface-card rounded-lg text-xs font-bold hover:bg-canvas-dark transition-colors shadow-sm">
                            <FaFileExcel className="text-success-600" /> Select Excel
                          </span>
                        </label>

                        {excelData && (
                          <span className="text-xs text-ink-500 font-semibold bg-surface-card border border-border-light px-2.5 py-1.5 rounded-lg shadow-sm">
                            {excelData.length} rows selected
                          </span>
                        )}

                        <Button
                          variant="success"
                          size="sm"
                          onClick={handleExcelUpload}
                          disabled={uploadingExcel || !excelData}
                        >
                          {uploadingExcel ? <Spinner size="sm" /> : <><FaUpload className="mr-1.5" /> Upload</>}
                        </Button>
                      </div>
                    </div>

                    {/* Transaction Analysis Table */}
                    {selectedOption.fk_personal_details_hasMany_transactions_customerUniqueId ? (
                      (() => {
                        const allTransactions = selectedOption.fk_personal_details_hasMany_transactions_customerUniqueId;
                        const {
                          transactions: allFormattedTransactions,
                          closingBalance: overallClosingBalance
                        } = calculateClosingBalance(allTransactions);

                        const filteredFormattedTransactions = filterTransactions(allFormattedTransactions);

                        const {
                          totalDeposits: filteredTotalDeposits,
                          totalWithdrawals: filteredTotalWithdrawals
                        } = calculateClosingBalance(filteredFormattedTransactions);

                        const sortedTransactions = [...filteredFormattedTransactions].sort((a, b) => {
                          const dateA = a.date ? new Date(a.date) : new Date(0);
                          const dateB = b.date ? new Date(b.date) : new Date(0);
                          const diffDate = dateB - dateA;
                          if (diffDate !== 0) return diffDate;

                          const createdA = a.createdAt ? new Date(a.createdAt) : new Date(0);
                          const createdB = b.createdAt ? new Date(b.createdAt) : new Date(0);
                          const diffCreated = createdB - createdA;
                          if (diffCreated !== 0) return diffCreated;

                          return (b.id || 0) - (a.id || 0);
                        });

                        const transactionsToShow = showAllTransactions ? sortedTransactions : sortedTransactions.slice(0, 10);

                        return (
                          <div className="space-y-4">
                            {/* Running Totals Row */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                              <StatCard
                                title="Total Deposits"
                                value={formatCurrency(filteredTotalDeposits)}
                                accent="success"
                                customBg="bg-[#E5F3E7]"
                                className="animated-glow-border border-glow-green"
                              />
                              <StatCard
                                title="Total Withdrawals"
                                value={formatCurrency(filteredTotalWithdrawals)}
                                accent="danger"
                                customBg="bg-[#FDF2F2]"
                                className="animated-glow-border border-glow-red"
                              />
                              <StatCard
                                title="Closing Balance"
                                value={formatCurrency(overallClosingBalance)}
                                accent="primary"
                                customBg="bg-[#ECEFF7]"
                                className="animated-glow-border border-glow-blue"
                              />
                            </div>

                            {/* Admin controls for selected transactions */}
                            {isSuperAdmin && transactionsToShow.length > 0 && (
                              <div className="flex gap-2 items-center flex-wrap bg-canvas-dark p-2.5 rounded-lg border border-dashed border-border-medium">
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={handleDeleteSelectedTxns}
                                  disabled={selectedTxnIds.length === 0 || deletingTxns}
                                  className="!border-danger-500/30 !text-danger-500 hover:!bg-danger-500/5"
                                >
                                  <FaTrash className="mr-1.5" /> Delete Selected ({selectedTxnIds.length})
                                </Button>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={handleDeleteAllTxns}
                                  disabled={deletingTxns}
                                >
                                  <FaTrashAlt className="mr-1.5" /> Delete All Transactions
                                </Button>
                              </div>
                            )}

                            {/* Transactions Table */}
                            <Table>
                              <Table.Head>
                                <tr>
                                  {isSuperAdmin && (
                                    <Table.HeadCell width="40px" align="center">
                                      <input
                                        type="checkbox"
                                        className="rounded border-border-medium text-primary focus:ring-primary/20"
                                        checked={transactionsToShow.length > 0 && selectedTxnIds.length === transactionsToShow.length}
                                        onChange={(e) => handleSelectAllTxns(transactionsToShow, e.target.checked)}
                                      />
                                    </Table.HeadCell>
                                  )}
                                  <Table.HeadCell>Date & Time</Table.HeadCell>
                                  <Table.HeadCell>Transaction ID</Table.HeadCell>
                                  <Table.HeadCell>Description</Table.HeadCell>
                                  <Table.HeadCell align="center">Cash Type</Table.HeadCell>
                                  <Table.HeadCell align="right">Credit (Deposit)</Table.HeadCell>
                                  <Table.HeadCell align="right">Debit (Withdrawal)</Table.HeadCell>
                                  <Table.HeadCell align="right">Closing Balance</Table.HeadCell>
                                  {isSuperAdmin && <Table.HeadCell align="center">Action</Table.HeadCell>}
                                </tr>
                              </Table.Head>
                              <Table.Body>
                                {transactionsToShow.length > 0 ? (
                                  transactionsToShow.map((transaction, index) => (
                                    <Table.Row key={index}>
                                      {isSuperAdmin && (
                                        <Table.Cell align="center">
                                          <input
                                            type="checkbox"
                                            className="rounded border-border-medium text-primary focus:ring-primary/20"
                                            checked={selectedTxnIds.includes(transaction.id)}
                                            onChange={() => handleToggleTxnSelect(transaction.id)}
                                          />
                                        </Table.Cell>
                                      )}
                                      <Table.Cell>
                                        <div className="font-semibold text-ink-900">
                                          {transaction.date ? new Date(transaction.date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A'}
                                        </div>
                                        <div className="text-[10px] text-ink-500 font-normal">
                                          {transaction.createdAt ? new Date(transaction.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }) : ''}
                                        </div>
                                      </Table.Cell>
                                      <Table.Cell className="font-mono text-ink-700">{transaction.transactionId || "N/A"}</Table.Cell>
                                      <Table.Cell className="text-ink-700">
                                        {/* Table.Cell is whitespace-nowrap, which beat the whitespace-normal
                                            class here, so long narrations (reversals, auto-deducts) ran over
                                            the Cash Type and amount columns. Wrap inside a fixed-width box;
                                            overflowWrap breaks long refs like REVERSAL-LOAN-3900-STMT-53616. */}
                                        <div style={{ whiteSpace: "normal", overflowWrap: "anywhere", width: "22rem", maxWidth: "22rem" }}>
                                          {transaction.narration || "N/A"}
                                        </div>
                                      </Table.Cell>
                                      <Table.Cell align="center">
                                        {transaction.cash_type ? (
                                          <Badge variant={transaction.cash_type === 'cash' ? 'warning' : 'info'} size="sm">
                                            {transaction.cash_type}
                                          </Badge>
                                        ) : 'N/A'}
                                      </Table.Cell>
                                      <Table.AmountCell align="right" className="text-success font-semibold">
                                        {transaction.credit ? transaction.credit.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : null}
                                      </Table.AmountCell>
                                      <Table.AmountCell align="right" className="text-danger font-semibold" negative>
                                        {transaction.debit ? transaction.debit.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : null}
                                      </Table.AmountCell>
                                      <Table.AmountCell align="right" className="text-ink-900 font-semibold">
                                        {transaction.closingBalance !== undefined ? (typeof transaction.closingBalance === "number" ? transaction.closingBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : transaction.closingBalance) : null}
                                      </Table.AmountCell>
                                      {isSuperAdmin && (
                                        <Table.ActionCell align="center">
                                          <Button
                                            variant="danger-ghost"
                                            size="sm"
                                            iconOnly
                                            onClick={() => handleDeleteSingleTxn(transaction.id)}
                                            title="Delete transaction"
                                          >
                                            <FaTrash className="w-3.5 h-3.5" />
                                          </Button>
                                        </Table.ActionCell>
                                      )}
                                    </Table.Row>
                                  ))
                                ) : (
                                  <Table.EmptyRow colSpan={isSuperAdmin ? 9 : 7} message="No transactions found for the selected date range." />
                                )}
                              </Table.Body>
                            </Table>

                            {sortedTransactions.length > 10 && (
                              <div className="flex justify-center mt-3">
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => setShowAllTransactions(!showAllTransactions)}
                                >
                                  {showAllTransactions ? <><FaChevronUp className="mr-1.5" /> View Less</> : <><FaChevronDown className="mr-1.5" /> View More</>}
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })()
                    ) : (
                      <EmptyState
                        variant="search"
                        title="No Transaction History Found"
                        description="This member doesn't have any recorded transactions yet."
                        size="sm"
                      />
                    )}
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Add Transaction Form (Non-Marginal Money) */}
            {showForm && accountType !== "01" && (
              <Card>
                <Card.Header title={
                  accountType === "02" ? "Add Fixed Deposit Transaction" :
                    accountType === "04" ? "Add Loan Transaction" :
                      accountType === "05" ? "Add Chit Transaction" : "Add Transaction"
                } />
                <Card.Body>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Transaction Type</label>
                      <select
                        value={transactionType}
                        onChange={(e) => setTransactionType(e.target.value)}
                        className="w-full border border-slate-300 rounded-lg p-2.5 bg-white text-sm focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="deposit">Deposit</option>
                        <option value="withdraw">Withdraw</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Amount</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="Enter Amount"
                        className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Narration</label>
                      <input
                        type="text"
                        value={narration}
                        onChange={(e) => setNarration(e.target.value)}
                        placeholder="Enter narration"
                        className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    {(accountType === "02" || accountType === "04") && (
                      <>
                        <div>
                          <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Interest Rate (%)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={interestRate}
                            onChange={(e) => setInterestRate(e.target.value)}
                            placeholder="Enter Interest Rate"
                            className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Tenure (Months)</label>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={tenure}
                            onChange={(e) => setTenure(e.target.value)}
                            placeholder="Enter Tenure"
                            className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-primary-500"
                          />
                        </div>
                      </>
                    )}

                    {accountType === "05" && (
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Chit Value</label>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={chitValue}
                          onChange={(e) => setChitValue(e.target.value)}
                          placeholder="Enter Chit Value"
                          className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Select Date</label>
                      <input
                        type="date"
                        max={today}
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Cash Type</label>
                      <select
                        value={cash_type}
                        onChange={(e) => setCash_type(e.target.value)}
                        className="w-full border border-slate-300 rounded-lg p-2.5 bg-white text-sm focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Select Cash Type</option>
                        <option value="online">Online</option>
                        <option value="cash">Cash</option>
                      </select>
                    </div>

                    {cash_type === "online" && (
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Reference Number</label>
                        <input
                          type="text"
                          value={referenceNumber}
                          onChange={(e) => setReferenceNumber(e.target.value)}
                          placeholder="Enter Reference Number"
                          className="w-full border border-slate-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end pt-4 border-t mt-4">
                    <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
                      {submitting ? <Spinner size="sm" /> : "Submit Transaction"}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            )}
          </div>
        )}

        {/* Global Document Modal */}
        <Modal
          isOpen={openDocumentModal}
          onClose={handleCloseDocument}
          title="Print Document"
          size="xl"
        >
          <div className="max-h-[80vh] overflow-y-auto bg-white rounded-lg">
            {renderDocumentContent()}
          </div>
        </Modal>

        {/* Marginal Money Denomination Confirm Modal */}
        <Modal
          isOpen={openConfirmModal}
          onClose={() => setOpenConfirmModal(false)}
          title="Confirm Denomination Mismatch"
          size="md"
        >
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              The entered amount (<strong>₹{parseFloat(amount || 0).toFixed(2)}</strong>) does not match the total denomination amount (<strong>₹{totalAmount.toFixed(2)}</strong>).
            </p>
            <p className="text-sm font-semibold text-slate-700">Do you want to proceed with the transaction anyway?</p>
          </div>
          <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
            <Button variant="secondary" onClick={() => setOpenConfirmModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={submitTransaction} disabled={submitting}>
              {submitting ? <Spinner size="sm" /> : "Confirm & Proceed"}
            </Button>
          </div>
        </Modal>

        {/* Marginal Money Transaction Entry Modal */}
        <Modal
          isOpen={openMarginalMoneyModal}
          onClose={() => setOpenMarginalMoneyModal(false)}
          title="Add Marginal Money Transaction"
          size="lg"
        >
          <div className="max-h-[80vh] overflow-y-auto">
            {renderMarginalMoneyForm()}
          </div>
        </Modal>

      </div>
    </PageLayout>
  );
};

export default MemberDetails;